import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Copy, Trash2, Pencil, Eye, Upload, Bold, Italic, AlignLeft, AlignCenter, AlignRight, Volume2 } from "lucide-react";
import { SOUND_OPTIONS as SOUND_OPTIONS_LIB, playSound as previewSound } from "@/lib/sounds";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import TranslatedInput from "@/components/admin/TranslatedInput";
import { FONT_FAMILIES, TEXT_ALIGNS, BG_GRADIENTS, BG_SOLIDS, BG_PATTERNS, getBackgroundCss, getQuestionTextStyle, DEFAULT_VISUAL, REVEAL_EFFECTS, CARD_SURFACE_PRESETS, getCardSurfaceGradient } from "@/lib/gameVisual";

const empty = {
  name: "",
  translations: {},
  format: "original",
  status: "training",
  mode: "normal",
  max_repeated_letters: 0,
  supert_count: 1,
  max_attempts: 3,
  lives: 1,
  time_penalty_seconds: 5,
  round_delay_ms: 100,
  simultaneous_ms: 500,
  active: true,
  active_languages: { pt: true, en: true, es: true },
  card_effect: "fade",
  roulette_effect: "slide_left",
  card_sound: "deal",
  card_sound_volume: 0.7,
  correct_sound: "correct",
  correct_sound_volume: 0.7,
  wrong_sound: "wrong",
  wrong_sound_volume: 0.7,
  victory_sound: "victory",
  victory_sound_volume: 0.8,
  defeat_sound: "defeat",
  defeat_sound_volume: 0.7,
  repeat_sound: "oops",
  repeat_sound_volume: 0.7,
  teacher_password: "",
  level_system: "levels",
  tiers: [],
  visual: { ...DEFAULT_VISUAL },
};

const CARD_EFFECTS = [
  { value: "fade", label: "Surgir (fade)" },
  { value: "flip", label: "Flip 3D" },
  { value: "slide", label: "Deslizar" },
  { value: "zoom", label: "Zoom" },
  { value: "none", label: "Sem efeito" },
];

const ROULETTE_EFFECTS = [
  { value: "slide_left", label: "Surgir da esquerda" },
  { value: "slide_right", label: "Surgir da direita" },
  { value: "fade", label: "Aparecer (fade)" },
  { value: "flip", label: "Flip 3D" },
  { value: "zoom", label: "Zoom" },
  { value: "none", label: "Sem efeito" },
];

const SOUND_OPTIONS = SOUND_OPTIONS_LIB;

export default function AdminGames() {
  const { t, lang } = useI18n();
  const [games, setGames] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [tab, setTab] = useState("basic");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [showPerspectivePreview, setShowPerspectivePreview] = useState(false);
  const [selected, setSelected] = useState(new Set());

  // Resolve a game's display name for the admin's CURRENT UI language.
  // IMPORTANT: the canonical `g.name` field IS the Portuguese (primary)
  // name; `translations.en` / `translations.es` only store overrides for
  // the other two locales. So PT resolves to `tr.pt?.name || g.name` and
  // the other locales fall through to the canonical PT name as a last
  // resort to avoid blank rows.
  const displayName = (g, lc = lang) => {
    const tr = g?.translations || {};
    const canonical = (g?.name || "").trim();
    if (lc === "pt") {
      return ((tr.pt?.name || "").trim() || canonical);
    }
    return ((tr[lc]?.name || "").trim() || canonical);
  };
  // Returns true when the game has NO translation registered for the
  // active admin UI language (and therefore the list is showing a
  // fallback value). For PT the canonical `g.name` counts as the PT
  // translation — only flag when it is truly empty.
  const missingTranslation = (g, lc = lang) => {
    const tr = g?.translations || {};
    if (lc === "pt") {
      const own = (tr.pt?.name || g?.name || "").trim();
      return !own;
    }
    const own = (tr[lc]?.name || "").trim();
    return !own;
  };
  // For each language, build { lowered_name -> count } so we can flag
  // ANY game whose translated name collides with another game's name in
  // the same language (user request 2026-02: avoid silent duplicates
  // when a game is replicated and the translations stay identical).
  const dupeMap = React.useMemo(() => {
    const out = { pt: new Map(), en: new Map(), es: new Map() };
    for (const g of games) {
      for (const lc of ["pt", "en", "es"]) {
        const n = displayName(g, lc).toLowerCase();
        if (!n) continue;
        out[lc].set(n, (out[lc].get(n) || 0) + 1);
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [games]);
  const duplicatedLangs = (g) => {
    const dups = [];
    for (const lc of ["pt", "en", "es"]) {
      const n = displayName(g, lc).toLowerCase();
      if (n && (dupeMap[lc].get(n) || 0) >= 2) dups.push(lc);
    }
    return dups;
  };

  const load = async () => {
    const { data } = await api.get("/games");
    setGames(data);
  };

  useEffect(() => { load(); }, []);

  const toggleSelect = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === games.length) setSelected(new Set());
    else setSelected(new Set(games.map((g) => g.id)));
  };

  const deleteSelected = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Eliminar ${selected.size} jogo(s) e respetivas perguntas/credenciais/resultados?`)) return;
    try {
      const ids = Array.from(selected);
      const { data } = await api.post("/games/bulk-delete", { ids });
      toast.success(`${data.deleted} eliminado(s)`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a eliminar");
    }
  };

  const deleteAll = async () => {
    if (!games.length) return;
    if (!window.confirm(`Eliminar TODOS os ${games.length} jogos? Esta ação remove também todas as perguntas/credenciais/resultados associados.`)) return;
    try {
      const ids = games.map((g) => g.id);
      const { data } = await api.post("/games/bulk-delete", { ids });
      toast.success(`${data.deleted} eliminado(s)`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a eliminar");
    }
  };

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/games/${editing.id}`, form);
        toast.success("Atualizado");
      } else {
        await api.post("/games", form);
        toast.success("Criado");
      }
      setOpen(false);
      setEditing(null);
      setForm(empty);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminar jogo (e perguntas/credenciais/resultados)?")) return;
    await api.delete(`/games/${id}`);
    toast.success("Eliminado");
    load();
  };

  const duplicate = async (id) => {
    // Pre-warn when the source game shares its translated name with any
    // other game in ANY language (so the admin realises the clone will
    // start life as a visually-indistinguishable copy). Users explicitly
    // requested this check (Feb 2026) after seeing two games showing the
    // same ES label on the public dropdown.
    const src = games.find((g) => g.id === id);
    if (src) {
      const srcDups = duplicatedLangs(src);
      if (srcDups.length > 0) {
        const ok = window.confirm(
          `Atenção: o jogo "${displayName(src)}" já tem o mesmo nome que outro em ${srcDups.join(", ").toUpperCase()}.\n\n` +
          "Vais criar uma cópia que vai ficar com nomes muito parecidos. " +
          "Depois de duplicar, abre o novo jogo e muda os nomes em todos os idiomas.\n\n" +
          "Continuar?"
        );
        if (!ok) return;
      }
    }
    await api.post(`/games/${id}/duplicate`);
    toast.success("Duplicado — não te esqueças de rever os nomes PT/EN/ES.");
    load();
  };

  const setVisual = (patch) => setForm((f) => ({ ...f, visual: { ...f.visual, ...patch } }));

  const onUploadImage = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Imagem demasiado grande (máx. 2 MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setVisual({ bg_type: "image", bg_image_url: ev.target.result });
    };
    reader.readAsDataURL(file);
  };

  const onUploadCardLogo = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Imagem demasiado grande (máx. 2 MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setVisual({ card_logo_image: ev.target.result });
    };
    reader.readAsDataURL(file);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-[Outfit] font-semibold text-slate-900">{t("games")}</h1>
        <Dialog
          open={open}
          onOpenChange={async (v) => {
            setOpen(v);
            if (!v) { setEditing(null); setForm(empty); setTab("basic"); return; }
            // Opening for a NEW game — prefill from the reference template
            // for the currently-selected format (defaults to "original").
            if (!editing) {
              try {
                const { data } = await api.get("/games/defaults", { params: { format: "original" } });
                setForm({ ...empty, ...data, name: "", tiers: [] });
              } catch (err) {
                console.debug("defaults fetch failed", err);
              }
            }
          }}
        >
          <DialogTrigger asChild>
            <Button data-testid="btn-create-game" className="bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4 mr-1" /> {t("create")}</Button>
          </DialogTrigger>
          <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
              <DialogHeader>
              <DialogTitle>{editing ? t("edit") : t("create")}</DialogTitle>
              <DialogDescription className="sr-only">Edição completa do jogo: nome, perguntas, escalões, perspetiva 3D, fundo, efeitos e sons.</DialogDescription>
            </DialogHeader>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-slate-200 -mt-2 mb-3">
              {[
                { id: "basic", label: "Geral" },
                { id: "levels", label: "Níveis / Escalões" },
                { id: "style", label: "Fonte das perguntas" },
                { id: "bg", label: "Fundo do jogo" },
                { id: "label", label: "Etiqueta Roleta" },
                { id: "perspective", label: "Perspetiva 3D" },
                { id: "card_logo", label: "Logotipo das cartas" },
                { id: "fx", label: "Efeitos/Sons" },
              ].map((tb) => (
                <button
                  key={tb.id}
                  data-testid={`tab-${tb.id}`}
                  onClick={() => setTab(tb.id)}
                  className={`px-3 py-2 text-sm font-medium border-b-2 ${tab === tb.id ? "border-sky-600 text-sky-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
                >
                  {tb.label}
                </button>
              ))}
              <div className="ml-auto">
                <Button type="button" variant="outline" size="sm" data-testid="btn-preview-game" onClick={() => setPreviewOpen(true)}>
                  <Eye className="w-4 h-4 mr-1" /> Previsualizar
                </Button>
              </div>
            </div>

            {tab === "basic" && (
              <div className="space-y-3">
                <TranslatedInput
                  label={t("name")}
                  field="name"
                  value={form.name}
                  translations={form.translations || {}}
                  onChange={({ value, translations }) => setForm({ ...form, name: value, translations })}
                  testid="game-name"
                />
                {/* Format selector. Locked AFTER the game exists because
                    questions, tiers and roulette config all depend on it. */}
                <div>
                  <Label className="flex items-center gap-2">
                    Formato
                    {editing && editing !== "new" && (
                      <span className="text-[10px] uppercase tracking-wider text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">bloqueado após criação</span>
                    )}
                  </Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {[
                      { code: "original", title: "ORIGINAL", meta: "5 níveis · A-J · 10 perguntas" },
                      { code: "quiz", title: "QUIZ", meta: "4 níveis · A-G · 7 perguntas" },
                    ].map((opt) => {
                      const active = (form.format || "original") === opt.code;
                      const locked = !!(editing && editing !== "new");
                      return (
                        <button
                          key={opt.code}
                          type="button"
                          disabled={locked}
                          onClick={async () => {
                            // Switching to QUIZ caps tier levels at 4 — clamp any existing
                            // tier with level=5 so the dropdown stays in sync. Visual hint
                            // lives below (see "tier-downgrade-warning").
                            const nextTiers = (form.tiers || []).map((t) => (
                              opt.code === "quiz" && (t.level || 1) > 4 ? { ...t, level: 4 } : t
                            ));
                            // For a NEW game, refresh defaults from the reference
                            // template for the newly-picked format (keeps `name` blank).
                            if (!editing) {
                              try {
                                const { data } = await api.get("/games/defaults", { params: { format: opt.code } });
                                setForm({ ...empty, ...data, name: form.name || "", tiers: nextTiers });
                                return;
                              } catch (err) {
                                console.debug("defaults fetch failed", err);
                              }
                            }
                            setForm({ ...form, format: opt.code, tiers: nextTiers });
                          }}
                          className={`relative text-left rounded-xl border-2 px-3 py-2 transition-colors ${
                            active ? "border-violet-500 bg-violet-50 ring-1 ring-violet-200" : "border-slate-200 bg-white hover:border-violet-300"
                          } ${locked ? "opacity-60 cursor-not-allowed" : ""}`}
                          data-testid={`game-format-${opt.code}`}
                        >
                          <div className="font-[Fredoka] font-bold text-sm text-slate-900">{opt.title}</div>
                          <div className="text-[11px] text-slate-500">{opt.meta}</div>
                          {active && <div className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-violet-500" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="training">{t("status_training")}</SelectItem>
                        <SelectItem value="competition">{t("status_competition")}</SelectItem>
                        <SelectItem value="eliminatorias" data-testid="game-status-eliminatorias">{t("status_eliminatorias")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Modo</Label>
                    <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                      <SelectTrigger data-testid="game-mode"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="normal" data-testid="game-mode-normal">{t("mode_normal")}</SelectItem>
                        <SelectItem value="sem_sorte" data-testid="game-mode-sem-sorte">{t("mode_sem_sorte")}</SelectItem>
                        <SelectItem value="sorte_ajustada" data-testid="game-mode-sorte-ajustada">Sorte ajustada</SelectItem>
                      </SelectContent>
                    </Select>
                    {form.mode === "sorte_ajustada" && (
                      <div className="mt-2 bg-amber-50 border border-amber-200 rounded-md p-2.5">
                        <Label className="text-xs font-bold text-amber-900">Máx. superletras repetidas no jogo</Label>
                        <Input
                          type="number"
                          min={0}
                          max={50}
                          value={form.max_repeated_letters ?? 0}
                          onChange={(e) => setForm({ ...form, max_repeated_letters: Math.max(0, parseInt(e.target.value || "0", 10)) })}
                          data-testid="game-max-repeats"
                          className="mt-1 bg-white"
                        />
                        <p className="text-[10px] text-amber-800 mt-1 leading-snug">
                          Limita o número total de superletras repetidas (S, U, P, E, R, T) durante todo o jogo. <b>0</b> = nenhuma repetição permitida. As superestrelas (★) mantêm a probabilidade normal.
                        </p>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label>SUPERT a completar</Label>
                    <Input type="number" min={1} max={10} value={form.supert_count} onChange={(e) => setForm({ ...form, supert_count: +e.target.value })} />
                  </div>
                  <div>
                    <Label>Máx. tentativas</Label>
                    <Input type="number" min={1} value={form.max_attempts} onChange={(e) => setForm({ ...form, max_attempts: +e.target.value })} />
                  </div>
                </div>
                {/* Lives + time penalty (per-game). lives=1 = sudden death (current
                    behavior). lives>1 = forgiving mode with time penalty per wrong. */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 rounded-xl bg-rose-50/60 border border-rose-200">
                  <div>
                    <Label className="flex items-center gap-1.5">
                      ❤️ Vidas <span className="text-rose-700 font-bold ml-1">{form.lives ?? 1}</span>
                      {(form.lives ?? 1) === 1 && <span className="text-[10px] uppercase tracking-wider bg-rose-200 text-rose-900 px-1.5 py-0.5 rounded font-bold">Morte Súbita</span>}
                    </Label>
                    <Input
                      type="number" min={1}
                      value={form.lives ?? 1}
                      onChange={(e) => setForm({ ...form, lives: Math.max(1, parseInt(e.target.value || "1", 10)) })}
                      data-testid="game-lives"
                    />
                    <p className="text-[11px] text-slate-500 mt-1">
                      <b>1</b> = morte súbita (jogo termina ao primeiro erro). <b>≥ 2</b> = jogador pode errar até <b>N-1</b> vezes; cada erro pinta a opção de vermelho, pausa o relógio, mostra um aviso e adiciona a penalização ao tempo.
                    </p>
                  </div>
                  <div>
                    <Label className="flex items-center gap-1.5">⏱ Penalização por erro (seg)</Label>
                    <Input
                      type="number" min={0} max={60}
                      value={form.time_penalty_seconds ?? 5}
                      onChange={(e) => setForm({ ...form, time_penalty_seconds: Math.max(0, Math.min(60, parseInt(e.target.value || "0", 10))) })}
                      disabled={(form.lives ?? 1) <= 1}
                      data-testid="game-time-penalty"
                    />
                    <p className="text-[11px] text-slate-500 mt-1">
                      Segundos adicionados ao tempo do jogo a cada erro (apenas quando Vidas ≥ 2).
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                  <div>
                    <Label className="flex items-center gap-1.5">⏳ Delay entre jogadas (ms)</Label>
                    <Input
                      type="number" min={0} max={5000} step={1}
                      value={form.round_delay_ms ?? 100}
                      onChange={(e) => setForm({ ...form, round_delay_ms: Math.max(0, Math.min(5000, parseInt(e.target.value || "0", 10))) })}
                      data-testid="game-round-delay"
                    />
                    <p className="text-[11px] text-slate-500 mt-1">
                      Tempo entre uma resposta e o surgir das opções da próxima pergunta.
                      Impede que o clique da pergunta actual apanhe a nova (especialmente em <b>VS Robot</b>).
                      Precisão de 1 milissegundo · valores típicos: 100-500 ms · máximo 5000 ms.
                    </p>
                  </div>
                  <div>
                    <Label className="flex items-center gap-1.5">🤝 Janela de resposta simultânea (ms) · VS Robot / VS Humano</Label>
                    <Input
                      type="number" min={0} max={2000} step={1}
                      value={form.simultaneous_ms ?? 500}
                      onChange={(e) => setForm({ ...form, simultaneous_ms: Math.max(0, Math.min(2000, parseInt(e.target.value || "0", 10))) })}
                      data-testid="game-simultaneous-ms"
                    />
                    <p className="text-[11px] text-slate-500 mt-1">
                      Se o clique do jogador e do robot/adversário caírem dentro desta janela, a ronda é considerada <b>simultânea</b> (ambos ganham/perdem).
                      Fora da janela, o primeiro a clicar leva a ronda.
                      Valores típicos: 300-700 ms · máximo 2000 ms.
                    </p>
                  </div>
                </div>
                <div className="rounded-xl border-2 border-slate-200 p-3 bg-slate-50">
                  <Label className="text-xs uppercase tracking-wider text-slate-600 font-bold mb-2 block">
                    Disponibilidade por idioma
                  </Label>
                  <p className="text-[11px] text-slate-500 mb-3">
                    Controla em que idiomas este jogo é visível para os jogadores. Um jogo pode estar ativo em PT mas inativo em EN.
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { code: "pt", label: "Português", flag: "🇵🇹" },
                      { code: "en", label: "English", flag: "🇬🇧" },
                      { code: "es", label: "Español", flag: "🇪🇸" },
                    ].map((l) => {
                      const isOn = !!(form.active_languages || {})[l.code];
                      return (
                        <button
                          key={l.code}
                          type="button"
                          onClick={() => {
                            const next = { ...(form.active_languages || {}), [l.code]: !isOn };
                            // Keep legacy `active` boolean true if any language is active.
                            setForm({ ...form, active_languages: next, active: Object.values(next).some(Boolean) });
                          }}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-colors text-sm font-bold ${
                            isOn
                              ? "border-emerald-400 bg-emerald-50 text-emerald-800"
                              : "border-slate-200 bg-white text-slate-500"
                          }`}
                          data-testid={`game-active-${l.code}`}
                        >
                          <span className="text-base">{l.flag}</span>
                          <span className="flex-1 text-left">{l.label}</span>
                          <span className={`w-2.5 h-2.5 rounded-full ${isOn ? "bg-emerald-500" : "bg-slate-300"}`} />
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <Label>Senha do professor (opcional) — se preenchida, será pedida antes de cada tentativa em competição</Label>
                  <Input
                    data-testid="game-teacher-password"
                    type="text"
                    placeholder="ex: PROF2026"
                    value={form.teacher_password || ""}
                    onChange={(e) => setForm({ ...form, teacher_password: e.target.value })}
                  />
                </div>
              </div>
            )}

            {tab === "levels" && (
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-slate-500">
                    Escolhe como os jogadores são agrupados neste jogo. <b>Sistema de Níveis</b> usa os 5 níveis padrão (1–5).
                    <b> Sistema de Escalões</b> permite criar subgrupos personalizados (ex.: &quot;6 ANOS&quot;, &quot;7 ANOS&quot;), cada um mapeado a um nível de jogo.
                    Quando ativado, todas as áreas (jogador, rankings, credenciais, diplomas) passam a mostrar o escalão em vez do nível.
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    data-testid="game-level-system-levels"
                    onClick={() => setForm({ ...form, level_system: "levels" })}
                    className={`flex-1 px-4 py-3 rounded-lg border-2 text-sm font-bold transition-colors ${form.level_system !== "tiers" ? "border-sky-500 bg-sky-50 text-sky-700" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}
                  >🎚️ Sistema de Níveis</button>
                  <button
                    type="button"
                    data-testid="game-level-system-tiers"
                    onClick={() => setForm({ ...form, level_system: "tiers" })}
                    className={`flex-1 px-4 py-3 rounded-lg border-2 text-sm font-bold transition-colors ${form.level_system === "tiers" ? "border-violet-500 bg-violet-50 text-violet-700" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}
                  >🏷️ Sistema de Escalões</button>
                </div>

                {form.level_system === "tiers" && (
                  <div className="border border-violet-200 bg-violet-50/40 rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-bold text-slate-800">Editor de Escalões</h4>
                        <p className="text-xs text-slate-500 mt-0.5">Cria, renomeia ou apaga escalões. Define a que nível de jogo cada escalão corresponde.</p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        data-testid="game-tier-add"
                        onClick={() => {
                          const newTier = { id: crypto.randomUUID?.() || `t-${Date.now()}`, name: `Escalão ${(form.tiers?.length || 0) + 1}`, level: 1, translations: {} };
                          setForm({ ...form, tiers: [...(form.tiers || []), newTier] });
                        }}
                      >+ Novo escalão</Button>
                    </div>
                    {(!form.tiers || form.tiers.length === 0) ? (
                      <div className="text-xs italic text-slate-500 py-6 text-center">Nenhum escalão criado ainda. Clica &quot;Novo escalão&quot; acima.</div>
                    ) : (
                      <div className="space-y-2">
                        {form.tiers.map((tier, idx) => (
                          <div key={tier.id} className="flex items-center gap-2 bg-white rounded-lg border border-violet-200 p-2" data-testid={`game-tier-${idx}`}>
                            <TranslatedInput
                              compact
                              field="name"
                              value={tier.name}
                              translations={tier.translations || {}}
                              onChange={({ value, translations }) => {
                                const next = [...form.tiers];
                                next[idx] = { ...next[idx], name: value, translations };
                                setForm({ ...form, tiers: next });
                              }}
                              placeholder="Nome do escalão"
                              testid={`game-tier-name-${idx}`}
                              className="flex-1"
                            />
                            <select
                              value={tier.level}
                              data-testid={`game-tier-level-${idx}`}
                              onChange={(e) => {
                                const next = [...form.tiers];
                                next[idx] = { ...next[idx], level: parseInt(e.target.value, 10) };
                                setForm({ ...form, tiers: next });
                              }}
                              className="border border-slate-300 rounded px-2 py-1 text-sm bg-white"
                            >
                              {(form.format === "quiz" ? [1, 2, 3, 4] : [1, 2, 3, 4, 5]).map((lvl) => (
                                <option key={lvl} value={lvl}>Nível {lvl}</option>
                              ))}
                            </select>
                            <button
                              type="button"
                              data-testid={`game-tier-delete-${idx}`}
                              onClick={() => setForm({ ...form, tiers: form.tiers.filter((_, i) => i !== idx) })}
                              className="text-red-500 hover:bg-red-50 rounded p-1.5"
                              title="Apagar escalão"
                            ><Trash2 className="w-4 h-4" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {tab === "style" && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Família da fonte</Label>
                    <Select value={form.visual.font_family} onValueChange={(v) => setVisual({ font_family: v })}>
                      <SelectTrigger data-testid="visual-font-family"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FONT_FAMILIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tamanho (px): {form.visual.font_size}</Label>
                    <input type="range" min={10} max={32} value={form.visual.font_size} onChange={(e) => setVisual({ font_size: +e.target.value })} className="w-full" data-testid="visual-font-size" />
                  </div>
                  <div>
                    <Label>Cor da fonte</Label>
                    <div className="flex gap-2 items-center">
                      <input type="color" value={form.visual.font_color} onChange={(e) => setVisual({ font_color: e.target.value })} className="h-10 w-14 rounded border" data-testid="visual-font-color" />
                      <Input value={form.visual.font_color} onChange={(e) => setVisual({ font_color: e.target.value })} className="flex-1" />
                    </div>
                  </div>
                  <div>
                    <Label>Alinhamento</Label>
                    <div className="flex gap-1">
                      {TEXT_ALIGNS.map((a) => {
                        const Icon = a.value === "left" ? AlignLeft : a.value === "right" ? AlignRight : AlignCenter;
                        return (
                          <button
                            key={a.value}
                            type="button"
                            data-testid={`visual-align-${a.value}`}
                            onClick={() => setVisual({ text_align: a.value })}
                            className={`flex-1 py-2 rounded border ${form.visual.text_align === a.value ? "bg-sky-100 border-sky-500 text-sky-700" : "bg-white border-slate-300 text-slate-600"}`}
                          >
                            <Icon className="w-4 h-4 mx-auto" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button type="button" data-testid="visual-bold" onClick={() => setVisual({ font_bold: !form.visual.font_bold })} className={`px-3 py-2 rounded border ${form.visual.font_bold ? "bg-sky-100 border-sky-500 text-sky-700" : "bg-white border-slate-300"}`}>
                    <Bold className="w-4 h-4" />
                  </button>
                  <button type="button" data-testid="visual-italic" onClick={() => setVisual({ font_italic: !form.visual.font_italic })} className={`px-3 py-2 rounded border ${form.visual.font_italic ? "bg-sky-100 border-sky-500 text-sky-700" : "bg-white border-slate-300"}`}>
                    <Italic className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-slate-500">Pré-visualização:</span>
                  <span className="ml-1 px-3 py-1.5 rounded bg-white border border-slate-200" style={getQuestionTextStyle(form.visual)}>
                    8 + 7
                  </span>
                </div>
              </div>
            )}

            {tab === "bg" && (
              <div className="space-y-3">
                <div>
                  <Label>Tipo de fundo</Label>
                  <div className="flex gap-2 mt-1">
                    {["gradient", "solid", "pattern", "image"].map((bt) => (
                      <button
                        key={bt}
                        type="button"
                        data-testid={`bg-type-${bt}`}
                        onClick={() => setVisual({ bg_type: bt, bg_value: bt === "gradient" ? "sky" : bt === "pattern" ? "stripes" : bt === "solid" ? "#dbeafe" : form.visual.bg_value })}
                        className={`flex-1 py-2 px-3 text-sm rounded border ${form.visual.bg_type === bt ? "bg-sky-100 border-sky-500 text-sky-700" : "bg-white border-slate-300"}`}
                      >
                        {bt === "gradient" ? "Gradiente" : bt === "solid" ? "Cor sólida" : bt === "pattern" ? "Padrão" : "Imagem"}
                      </button>
                    ))}
                  </div>
                </div>

                {form.visual.bg_type === "gradient" && (
                  <div className="grid grid-cols-4 gap-2">
                    {BG_GRADIENTS.map((g) => (
                      <button
                        key={g.id}
                        type="button"
                        data-testid={`bg-grad-${g.id}`}
                        onClick={() => setVisual({ bg_value: g.id })}
                        className={`h-16 rounded-lg border-2 ${form.visual.bg_value === g.id ? "border-slate-900 ring-2 ring-sky-300" : "border-slate-200"}`}
                        style={{ background: g.css }}
                        title={g.label}
                      >
                        <span className="text-[10px] text-white font-bold drop-shadow">{g.label}</span>
                      </button>
                    ))}
                  </div>
                )}

                {form.visual.bg_type === "solid" && (
                  <div className="grid grid-cols-4 gap-2">
                    {BG_SOLIDS.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        data-testid={`bg-solid-${c.id}`}
                        onClick={() => setVisual({ bg_value: c.id })}
                        className={`h-12 rounded-lg border-2 ${form.visual.bg_value === c.id ? "border-slate-900 ring-2 ring-sky-300" : "border-slate-200"}`}
                        style={{ background: c.id }}
                      />
                    ))}
                    <div className="col-span-4 flex items-center gap-2 mt-2">
                      <span className="text-xs text-slate-600">Personalizada:</span>
                      <input type="color" value={form.visual.bg_value.startsWith("#") ? form.visual.bg_value : "#ffffff"} onChange={(e) => setVisual({ bg_value: e.target.value })} className="h-9 w-12 rounded border" />
                      <Input value={form.visual.bg_value} onChange={(e) => setVisual({ bg_value: e.target.value })} className="flex-1" />
                    </div>
                  </div>
                )}

                {form.visual.bg_type === "pattern" && (
                  <div className="grid grid-cols-3 gap-2">
                    {BG_PATTERNS.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        data-testid={`bg-pat-${p.id}`}
                        onClick={() => setVisual({ bg_value: p.id })}
                        className={`h-20 rounded-lg border-2 ${form.visual.bg_value === p.id ? "border-slate-900 ring-2 ring-sky-300" : "border-slate-200"}`}
                        style={{ background: p.css }}
                      >
                        <span className="text-[10px] text-slate-700 font-bold bg-white/80 px-1 rounded">{p.label}</span>
                      </button>
                    ))}
                  </div>
                )}

                {form.visual.bg_type === "image" && (
                  <div className="space-y-2">
                    <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 bg-sky-100 text-sky-700 rounded border border-sky-300 hover:bg-sky-200">
                      <Upload className="w-4 h-4" /> Carregar imagem (máx 2 MB)
                      <input type="file" accept="image/*" className="hidden" data-testid="bg-image-upload" onChange={(e) => onUploadImage(e.target.files?.[0])} />
                    </label>
                    {form.visual.bg_image_url && (
                      <div
                        className="h-32 rounded-lg border border-slate-200 grid place-items-center"
                        style={{ background: getBackgroundCss(form.visual), backgroundSize: "cover", backgroundPosition: "center" }}
                      >
                        <span className="text-white drop-shadow bg-black/30 px-2 py-0.5 text-xs rounded">Pré-visualização</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Card borders (rebordo) per card type */}
                <div className="pt-4 border-t border-slate-200">
                  <div className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2">Rebordo das cartas</div>
                  <p className="text-xs text-slate-500 mb-3">Configura a espessura e cor do rebordo exterior, independente por cada tipo de carta.</p>

                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Card Front (carta de pergunta) */}
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 space-y-2">
                      <div className="text-xs font-bold text-slate-700">Carta de Pergunta (Frente)</div>
                      <div>
                        <Label>Espessura (px): {form.visual.card_front_border_width ?? 22}</Label>
                        <input
                          type="range"
                          min={0}
                          max={50}
                          value={form.visual.card_front_border_width ?? 22}
                          onChange={(e) => setVisual({ card_front_border_width: +e.target.value })}
                          className="w-full"
                          data-testid="visual-front-border-width"
                        />
                      </div>
                      <div>
                        <Label>Cor</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={form.visual.card_front_border_color || "#03060f"}
                            onChange={(e) => setVisual({ card_front_border_color: e.target.value })}
                            className="h-10 w-14 rounded border"
                            data-testid="visual-front-border-color"
                          />
                          <Input
                            value={form.visual.card_front_border_color || "#03060f"}
                            onChange={(e) => setVisual({ card_front_border_color: e.target.value })}
                            className="flex-1"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Roulette Card */}
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 space-y-2">
                      <div className="text-xs font-bold text-slate-700">Carta de Roleta</div>
                      <div>
                        <Label>Espessura (px): {form.visual.roulette_border_width ?? 22}</Label>
                        <input
                          type="range"
                          min={0}
                          max={50}
                          value={form.visual.roulette_border_width ?? 22}
                          onChange={(e) => setVisual({ roulette_border_width: +e.target.value })}
                          className="w-full"
                          data-testid="visual-roulette-border-width"
                        />
                      </div>
                      <div>
                        <Label>Cor</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={form.visual.roulette_border_color || "#03060f"}
                            onChange={(e) => setVisual({ roulette_border_color: e.target.value })}
                            className="h-10 w-14 rounded border"
                            data-testid="visual-roulette-border-color"
                          />
                          <Input
                            value={form.visual.roulette_border_color || "#03060f"}
                            onChange={(e) => setVisual({ roulette_border_color: e.target.value })}
                            className="flex-1"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card surface (inner blue body) color */}
                <div className="pt-4 border-t border-slate-200">
                  <div className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2">Cor das cartas (corpo interior)</div>
                  <p className="text-xs text-slate-500 mb-3">Escolhe a cor base do interior das cartas. Os 3 stops do gradiente são gerados automaticamente (mais claro no centro, mais escuro nas bordas).</p>

                  <div className="flex flex-wrap gap-2 mb-3">
                    {CARD_SURFACE_PRESETS.map((p) => {
                      const active = (form.visual.card_surface_color || "#173b78").toLowerCase() === p.color.toLowerCase();
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setVisual({ card_surface_color: p.color })}
                          className={`w-12 h-12 rounded-lg border-2 transition-all ${active ? "border-amber-500 scale-110 shadow-md" : "border-slate-200 hover:scale-105"}`}
                          style={{ background: getCardSurfaceGradient(p.color) }}
                          title={p.label}
                          data-testid={`visual-card-surface-preset-${p.id}`}
                        />
                      );
                    })}
                  </div>
                  <div className="grid grid-cols-[auto_1fr_1fr] gap-2 items-end">
                    <div>
                      <Label>Cor</Label>
                      <input
                        type="color"
                        value={form.visual.card_surface_color || "#173b78"}
                        onChange={(e) => setVisual({ card_surface_color: e.target.value })}
                        className="h-10 w-16 rounded border"
                        data-testid="visual-card-surface-color-picker"
                      />
                    </div>
                    <div>
                      <Label>Código (hex)</Label>
                      <Input
                        value={form.visual.card_surface_color || "#173b78"}
                        onChange={(e) => setVisual({ card_surface_color: e.target.value })}
                        placeholder="#173b78"
                        data-testid="visual-card-surface-color-hex"
                      />
                    </div>
                    <div>
                      <Label>Pré-visualização</Label>
                      <div
                        className="h-10 rounded-lg border border-slate-200"
                        style={{ background: getCardSurfaceGradient(form.visual.card_surface_color) }}
                      />
                    </div>
                  </div>
                </div>

                {/* Mini-grid A-J text style (Roulette left column) */}
                <div className="pt-4 border-t border-slate-200">
                  <div className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2">Mini-grid A-J (lado esquerdo da Roleta)</div>
                  <p className="text-xs text-slate-500 mb-3">Estilo do texto pequeno nas 10 caixas brancas (A-J) que ecoam as opções das perguntas.</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div>
                        <Label>Tipo de fonte</Label>
                        <Select value={form.visual.roulette_mini_font_family || "Fredoka"} onValueChange={(v) => setVisual({ roulette_mini_font_family: v })}>
                          <SelectTrigger data-testid="visual-mini-font-family"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {FONT_FAMILIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Tamanho (px): {form.visual.roulette_mini_font_size ?? 9}</Label>
                        <input
                          type="range"
                          min={6}
                          max={22}
                          value={form.visual.roulette_mini_font_size ?? 9}
                          onChange={(e) => setVisual({ roulette_mini_font_size: +e.target.value })}
                          className="w-full"
                          data-testid="visual-mini-font-size"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div>
                        <Label>Cor</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={form.visual.roulette_mini_font_color || "#334155"}
                            onChange={(e) => setVisual({ roulette_mini_font_color: e.target.value })}
                            className="h-10 w-14 rounded border"
                            data-testid="visual-mini-font-color"
                          />
                          <Input
                            value={form.visual.roulette_mini_font_color || "#334155"}
                            onChange={(e) => setVisual({ roulette_mini_font_color: e.target.value })}
                            className="flex-1"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2 pt-5">
                        <input
                          id="mini-bold"
                          type="checkbox"
                          checked={form.visual.roulette_mini_font_bold ?? true}
                          onChange={(e) => setVisual({ roulette_mini_font_bold: e.target.checked })}
                          data-testid="visual-mini-font-bold"
                        />
                        <Label htmlFor="mini-bold" className="cursor-pointer">Negrito</Label>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 bg-slate-50 rounded-lg p-3 border border-slate-200">
                    <div className="text-xs font-bold text-slate-700 mb-2">Letra/Estrela grande (caixa de revelação)</div>
                    <Label>Tamanho (% da caixa): {form.visual.roulette_reveal_size ?? 115}</Label>
                    <input
                      type="range"
                      min={50}
                      max={180}
                      value={form.visual.roulette_reveal_size ?? 115}
                      onChange={(e) => setVisual({ roulette_reveal_size: +e.target.value })}
                      className="w-full"
                      data-testid="visual-reveal-size"
                    />
                    <p className="text-[11px] text-slate-500 mt-1 mb-3">Controla quão grande aparece a letra (S/U/P/E/R/T/★) na caixa branca grande da roleta. 100% = largura da caixa.</p>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Deslocamento horizontal (px): {form.visual.roulette_reveal_offset_x ?? 0}</Label>
                        <input
                          type="range"
                          min={-60}
                          max={60}
                          value={form.visual.roulette_reveal_offset_x ?? 0}
                          onChange={(e) => setVisual({ roulette_reveal_offset_x: +e.target.value })}
                          className="w-full"
                          data-testid="visual-reveal-offset-x"
                        />
                        <p className="text-[11px] text-slate-500">Negativo = esquerda · Positivo = direita</p>
                      </div>
                      <div>
                        <Label>Deslocamento vertical (px): {form.visual.roulette_reveal_offset_y ?? 0}</Label>
                        <input
                          type="range"
                          min={-60}
                          max={60}
                          value={form.visual.roulette_reveal_offset_y ?? 0}
                          onChange={(e) => setVisual({ roulette_reveal_offset_y: +e.target.value })}
                          className="w-full"
                          data-testid="visual-reveal-offset-y"
                        />
                        <p className="text-[11px] text-slate-500">Negativo = cima · Positivo = baixo</p>
                      </div>
                    </div>

                    <div className="mt-4 pt-3 border-t border-slate-200">
                      <div className="flex items-center gap-3 mb-2">
                        <Switch
                          checked={form.visual.roulette_reveal_effect_enabled ?? true}
                          onCheckedChange={(v) => setVisual({ roulette_reveal_effect_enabled: v })}
                          data-testid="visual-reveal-effect-enabled"
                        />
                        <Label className="cursor-pointer">Animação de entrada da letra/estrela</Label>
                      </div>
                      {form.visual.roulette_reveal_effect_enabled !== false && (
                        <div>
                          <Label>Tipo de efeito</Label>
                          <Select
                            value={form.visual.roulette_reveal_effect || "spring"}
                            onValueChange={(v) => setVisual({ roulette_reveal_effect: v })}
                          >
                            <SelectTrigger data-testid="visual-reveal-effect"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {REVEAL_EFFECTS.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <p className="text-[11px] text-slate-500 mt-2">Quando desligado, a letra/estrela surge estática, como parte integrante da carta.</p>
                    </div>
                  </div>

                  {/* Outline (contorno) — small + reveal letters */}
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2">Contorno das letras</div>
                    <p className="text-xs text-slate-500 mb-3">Cor e espessura do contorno externo (efeito degradé). Independente para letras pequenas A-J, letras do ciclo Multi-SUPERT e letra/estrela grande.</p>
                    <div className="grid md:grid-cols-3 gap-4">
                      {/* Small letters */}
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 space-y-2">
                        <div className="text-xs font-bold text-slate-700">Letras pequenas (A-J)</div>
                        <div>
                          <Label>Tamanho mini (px): {form.visual.small_letter_size ?? 26}</Label>
                          <input
                            type="range" min={12} max={64} step={1}
                            value={form.visual.small_letter_size ?? 26}
                            onChange={(e) => setVisual({ small_letter_size: parseInt(e.target.value, 10) })}
                            data-testid="visual-small-letter-size"
                            className="w-full"
                          />
                          <p className="text-[11px] text-slate-500 mt-1">Letras A-J na roleta mini (accompanha o snippet).</p>
                        </div>
                        <div>
                          <Label>Tamanho grande (px): {form.visual.front_letter_size ?? 48}</Label>
                          <input
                            type="range" min={24} max={120} step={1}
                            value={form.visual.front_letter_size ?? 48}
                            onChange={(e) => setVisual({ front_letter_size: parseInt(e.target.value, 10) })}
                            data-testid="visual-front-letter-size"
                            className="w-full"
                          />
                          <p className="text-[11px] text-slate-500 mt-1">Letras A-J grandes ao lado de cada opção (as das caixas do teu print).</p>
                        </div>
                        <div>
                          <Label>Espessura (px): {(form.visual.small_letter_outline_width ?? 0.5).toFixed(1)}</Label>
                          <input
                            type="range"
                            min={0}
                            max={3}
                            step={0.1}
                            value={form.visual.small_letter_outline_width ?? 0.5}
                            onChange={(e) => setVisual({ small_letter_outline_width: parseFloat(e.target.value) })}
                            className="w-full"
                            data-testid="visual-small-outline-width"
                          />
                        </div>
                        <div>
                          <Label>Modo</Label>
                          <Select
                            value={form.visual.small_letter_outline_mode || "solid"}
                            onValueChange={(v) => setVisual({ small_letter_outline_mode: v })}
                          >
                            <SelectTrigger data-testid="visual-small-outline-mode"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="solid">Sólido (1 cor)</SelectItem>
                              <SelectItem value="gradient">Degradé (2 cores)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Cor {form.visual.small_letter_outline_mode === "gradient" ? "(topo/esquerda)" : ""}</Label>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              value={form.visual.small_letter_outline_color || "#000000"}
                              onChange={(e) => setVisual({ small_letter_outline_color: e.target.value })}
                              className="h-10 w-14 rounded border"
                              data-testid="visual-small-outline-color"
                            />
                            <Input
                              value={form.visual.small_letter_outline_color || "#000000"}
                              onChange={(e) => setVisual({ small_letter_outline_color: e.target.value })}
                              className="flex-1"
                            />
                          </div>
                        </div>
                        {form.visual.small_letter_outline_mode === "gradient" && (
                          <div>
                            <Label>Cor secundária (base/direita)</Label>
                            <div className="flex gap-2">
                              <input
                                type="color"
                                value={form.visual.small_letter_outline_color2 || "#666666"}
                                onChange={(e) => setVisual({ small_letter_outline_color2: e.target.value })}
                                className="h-10 w-14 rounded border"
                                data-testid="visual-small-outline-color2"
                              />
                              <Input
                                value={form.visual.small_letter_outline_color2 || "#666666"}
                                onChange={(e) => setVisual({ small_letter_outline_color2: e.target.value })}
                                className="flex-1"
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Cycle letters (Multi-SUPERT slots) */}
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 space-y-2">
                        <div className="text-xs font-bold text-slate-700">Letras de ciclo (Multi-SUPERT)</div>
                        <div>
                          <Label>Tamanho (% da célula): {form.visual.cycle_letter_size ?? 85}</Label>
                          <input
                            type="range" min={40} max={120} step={1}
                            value={form.visual.cycle_letter_size ?? 85}
                            onChange={(e) => setVisual({ cycle_letter_size: parseInt(e.target.value, 10) })}
                            data-testid="visual-cycle-letter-size"
                            className="w-full"
                          />
                          <p className="text-[11px] text-slate-500 mt-1">Tamanho das letras D, C, G, C... na faixa superior da roleta (% da largura da célula).</p>
                        </div>
                        <div>
                          <Label>Espessura (px): {(form.visual.cycle_letter_outline_width ?? 0.5).toFixed(1)}</Label>
                          <input
                            type="range"
                            min={0}
                            max={3}
                            step={0.1}
                            value={form.visual.cycle_letter_outline_width ?? 0.5}
                            onChange={(e) => setVisual({ cycle_letter_outline_width: parseFloat(e.target.value) })}
                            className="w-full"
                            data-testid="visual-cycle-outline-width"
                          />
                        </div>
                        <div>
                          <Label>Modo</Label>
                          <Select
                            value={form.visual.cycle_letter_outline_mode || "solid"}
                            onValueChange={(v) => setVisual({ cycle_letter_outline_mode: v })}
                          >
                            <SelectTrigger data-testid="visual-cycle-outline-mode"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="solid">Sólido (1 cor)</SelectItem>
                              <SelectItem value="gradient">Degradé (2 cores)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Cor {form.visual.cycle_letter_outline_mode === "gradient" ? "(topo/esquerda)" : ""}</Label>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              value={form.visual.cycle_letter_outline_color || "#000000"}
                              onChange={(e) => setVisual({ cycle_letter_outline_color: e.target.value })}
                              className="h-10 w-14 rounded border"
                              data-testid="visual-cycle-outline-color"
                            />
                            <Input
                              value={form.visual.cycle_letter_outline_color || "#000000"}
                              onChange={(e) => setVisual({ cycle_letter_outline_color: e.target.value })}
                              className="flex-1"
                            />
                          </div>
                        </div>
                        {form.visual.cycle_letter_outline_mode === "gradient" && (
                          <div>
                            <Label>Cor secundária (base/direita)</Label>
                            <div className="flex gap-2">
                              <input
                                type="color"
                                value={form.visual.cycle_letter_outline_color2 || "#666666"}
                                onChange={(e) => setVisual({ cycle_letter_outline_color2: e.target.value })}
                                className="h-10 w-14 rounded border"
                                data-testid="visual-cycle-outline-color2"
                              />
                              <Input
                                value={form.visual.cycle_letter_outline_color2 || "#666666"}
                                onChange={(e) => setVisual({ cycle_letter_outline_color2: e.target.value })}
                                className="flex-1"
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Reveal big letter */}
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 space-y-2">
                        <div className="text-xs font-bold text-slate-700">Letra/Estrela grande</div>
                        <div>
                          <Label>Espessura (px): {(form.visual.reveal_outline_width ?? 0.5).toFixed(1)}</Label>
                          <input
                            type="range"
                            min={0}
                            max={5}
                            step={0.1}
                            value={form.visual.reveal_outline_width ?? 0.5}
                            onChange={(e) => setVisual({ reveal_outline_width: parseFloat(e.target.value) })}
                            className="w-full"
                            data-testid="visual-reveal-outline-width"
                          />
                        </div>
                        <div>
                          <Label>Modo</Label>
                          <Select
                            value={form.visual.reveal_outline_mode || "solid"}
                            onValueChange={(v) => setVisual({ reveal_outline_mode: v })}
                          >
                            <SelectTrigger data-testid="visual-reveal-outline-mode"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="solid">Sólido (1 cor)</SelectItem>
                              <SelectItem value="gradient">Degradé (2 cores)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Cor {form.visual.reveal_outline_mode === "gradient" ? "(topo/esquerda)" : ""}</Label>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              value={form.visual.reveal_outline_color || "#000000"}
                              onChange={(e) => setVisual({ reveal_outline_color: e.target.value })}
                              className="h-10 w-14 rounded border"
                              data-testid="visual-reveal-outline-color"
                            />
                            <Input
                              value={form.visual.reveal_outline_color || "#000000"}
                              onChange={(e) => setVisual({ reveal_outline_color: e.target.value })}
                              className="flex-1"
                            />
                          </div>
                        </div>
                        {form.visual.reveal_outline_mode === "gradient" && (
                          <div>
                            <Label>Cor secundária (base/direita)</Label>
                            <div className="flex gap-2">
                              <input
                                type="color"
                                value={form.visual.reveal_outline_color2 || "#666666"}
                                onChange={(e) => setVisual({ reveal_outline_color2: e.target.value })}
                                className="h-10 w-14 rounded border"
                                data-testid="visual-reveal-outline-color2"
                              />
                              <Input
                                value={form.visual.reveal_outline_color2 || "#666666"}
                                onChange={(e) => setVisual({ reveal_outline_color2: e.target.value })}
                                className="flex-1"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {tab === "label" && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <input
                    type="checkbox"
                    data-testid="rl-enabled"
                    checked={form.visual.roulette_label_enabled !== false}
                    onChange={(e) => setVisual({ roulette_label_enabled: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium text-slate-700">Mostrar etiqueta por cima da Carta Roleta</span>
                </div>
                <p className="text-xs text-slate-500">Aparece em cima da carta da Roleta com leve rotação. Configura o texto por idioma e o estilo visual abaixo.</p>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Texto (PT)</Label>
                    <Input
                      data-testid="rl-text-pt"
                      value={form.visual.roulette_label_text_pt ?? "CARTA ROLETA"}
                      onChange={(e) => setVisual({ roulette_label_text_pt: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Texto (EN)</Label>
                    <Input
                      data-testid="rl-text-en"
                      value={form.visual.roulette_label_text_en ?? "SELECTOR CARD"}
                      onChange={(e) => setVisual({ roulette_label_text_en: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Texto (ES)</Label>
                    <Input
                      data-testid="rl-text-es"
                      value={form.visual.roulette_label_text_es ?? "CARTA RULETA"}
                      onChange={(e) => setVisual({ roulette_label_text_es: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Cor do texto</Label>
                    <div className="flex gap-2 mt-1">
                      <input type="color" value={form.visual.roulette_label_color ?? "#0F172A"} onChange={(e) => setVisual({ roulette_label_color: e.target.value })} className="h-9 w-12 rounded border" />
                      <Input data-testid="rl-color" value={form.visual.roulette_label_color ?? "#0F172A"} onChange={(e) => setVisual({ roulette_label_color: e.target.value })} className="flex-1" />
                    </div>
                  </div>
                  <div>
                    <Label>Cor do fundo</Label>
                    <div className="flex gap-2 mt-1">
                      <input type="color" value={form.visual.roulette_label_bg_color ?? "#FFFFFF"} onChange={(e) => setVisual({ roulette_label_bg_color: e.target.value })} className="h-9 w-12 rounded border" />
                      <Input data-testid="rl-bg" value={form.visual.roulette_label_bg_color ?? "#FFFFFF"} onChange={(e) => setVisual({ roulette_label_bg_color: e.target.value })} className="flex-1" />
                    </div>
                  </div>
                  <div>
                    <Label>Cor do contorno</Label>
                    <div className="flex gap-2 mt-1">
                      <input type="color" value={form.visual.roulette_label_border_color ?? "#0F172A"} onChange={(e) => setVisual({ roulette_label_border_color: e.target.value })} className="h-9 w-12 rounded border" />
                      <Input data-testid="rl-border-color" value={form.visual.roulette_label_border_color ?? "#0F172A"} onChange={(e) => setVisual({ roulette_label_border_color: e.target.value })} className="flex-1" />
                    </div>
                  </div>
                  <div>
                    <Label>Espessura do contorno (px): {form.visual.roulette_label_border_width ?? 0}</Label>
                    <input type="range" min="0" max="6" step="1" value={form.visual.roulette_label_border_width ?? 0} onChange={(e) => setVisual({ roulette_label_border_width: +e.target.value })} className="w-full" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Família da fonte</Label>
                    <Select value={form.visual.roulette_label_font_family ?? "Fredoka"} onValueChange={(v) => setVisual({ roulette_label_font_family: v })}>
                      <SelectTrigger data-testid="rl-font"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FONT_FAMILIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tamanho da fonte (px): {form.visual.roulette_label_font_size ?? 14}</Label>
                    <input type="range" min="8" max="32" step="1" value={form.visual.roulette_label_font_size ?? 14} onChange={(e) => setVisual({ roulette_label_font_size: +e.target.value })} className="w-full" />
                  </div>
                </div>

                <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <input
                    type="checkbox"
                    data-testid="rl-bold"
                    checked={form.visual.roulette_label_font_bold !== false}
                    onChange={(e) => setVisual({ roulette_label_font_bold: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium text-slate-700">Negrito</span>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Padding horizontal (px): {form.visual.roulette_label_padding_x ?? 18}</Label>
                    <input type="range" min="4" max="40" step="1" value={form.visual.roulette_label_padding_x ?? 18} onChange={(e) => setVisual({ roulette_label_padding_x: +e.target.value })} className="w-full" />
                  </div>
                  <div>
                    <Label>Padding vertical (px): {form.visual.roulette_label_padding_y ?? 6}</Label>
                    <input type="range" min="2" max="20" step="1" value={form.visual.roulette_label_padding_y ?? 6} onChange={(e) => setVisual({ roulette_label_padding_y: +e.target.value })} className="w-full" />
                  </div>
                  <div>
                    <Label>Rotação (graus): {form.visual.roulette_label_rotation ?? -8}</Label>
                    <input type="range" min="-30" max="30" step="1" value={form.visual.roulette_label_rotation ?? -8} onChange={(e) => setVisual({ roulette_label_rotation: +e.target.value })} className="w-full" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Deslocação X (px): {form.visual.roulette_label_offset_x ?? 0}</Label>
                    <input type="range" min="-80" max="80" step="1" value={form.visual.roulette_label_offset_x ?? 0} onChange={(e) => setVisual({ roulette_label_offset_x: +e.target.value })} className="w-full" />
                  </div>
                  <div>
                    <Label>Deslocação Y (px): {form.visual.roulette_label_offset_y ?? -8}</Label>
                    <input type="range" min="-40" max="40" step="1" value={form.visual.roulette_label_offset_y ?? -8} onChange={(e) => setVisual({ roulette_label_offset_y: +e.target.value })} className="w-full" />
                  </div>
                </div>

                {/* Preview */}
                <div className="border-t border-slate-200 pt-3 mt-2">
                  <Label className="text-xs uppercase text-slate-500">Pré-visualização</Label>
                  <div className="mt-2 bg-slate-100 rounded-xl h-32 flex items-center justify-center relative overflow-hidden">
                    {form.visual.roulette_label_enabled !== false && (
                      <div
                        style={{
                          background: form.visual.roulette_label_bg_color ?? "#FFFFFF",
                          color: form.visual.roulette_label_color ?? "#0F172A",
                          border: `${form.visual.roulette_label_border_width ?? 0}px solid ${form.visual.roulette_label_border_color ?? "#0F172A"}`,
                          fontFamily: `"${form.visual.roulette_label_font_family ?? "Fredoka"}", sans-serif`,
                          fontSize: `${form.visual.roulette_label_font_size ?? 14}px`,
                          fontWeight: (form.visual.roulette_label_font_bold ?? true) ? 900 : 500,
                          padding: `${form.visual.roulette_label_padding_y ?? 6}px ${form.visual.roulette_label_padding_x ?? 18}px`,
                          borderRadius: 999,
                          boxShadow: "0 6px 14px rgba(0,0,0,.18)",
                          letterSpacing: "0.5px",
                          transform: `rotate(${form.visual.roulette_label_rotation ?? -8}deg)`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {form.visual.roulette_label_text_pt ?? "CARTA ROLETA"}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {tab === "perspective" && (
              <div className="space-y-5">
                <p className="text-sm text-slate-500">
                  Controla a inclinação 3D das cartas durante o jogo. As cartas convergem para o centro (estilo «livro aberto») com sombras suaves.
                  Valor 0 = sem inclinação (cartas planas).
                </p>

                <div className="rounded-xl border border-slate-200 p-4 bg-white space-y-3">
                  <div>
                    <h4 className="font-bold text-slate-800">Números das mini-cartas (SUPERT bar)</h4>
                    <p className="text-xs text-slate-500 mt-0.5">Cada slot da barra SUPERT mostra uma mini-carta com a grelha A-J. Define o número exibido em cada posição para este jogo.</p>
                  </div>
                  <div className="grid grid-cols-5 gap-2">
                    {["A","B","C","D","E","F","G","H","I","J"].map((pos, idx) => {
                      const arr = Array.isArray(form.visual.card_position_numbers) && form.visual.card_position_numbers.length === 10
                        ? form.visual.card_position_numbers
                        : [5,1,9,14,8,13,13,6,61,76];
                      return (
                        <div key={pos} className="flex items-center gap-1.5">
                          <span className="w-5 grid place-items-center font-[Fredoka] font-extrabold text-amber-600 bg-amber-100 rounded text-xs">{pos}</span>
                          <input
                            type="number"
                            min={0}
                            max={9999}
                            data-testid={`game-card-num-${pos}`}
                            value={arr[idx]}
                            onChange={(e) => {
                              const next = [...arr];
                              next[idx] = parseInt(e.target.value || "0", 10) || 0;
                              setVisual({ card_position_numbers: next });
                            }}
                            className="flex-1 min-w-0 border border-slate-300 rounded px-1.5 py-1 text-xs text-center font-mono"
                          />
                        </div>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    onClick={() => setVisual({ card_position_numbers: [5,1,9,14,8,13,13,6,61,76] })}
                    data-testid="game-card-num-reset"
                    className="text-[11px] font-bold text-slate-500 hover:text-slate-700 underline"
                  >Repor predefinidos (5,1,9,14,8 · 13,13,6,61,76)</button>
                </div>

                {/* Gradients on top strip of each cycle/level cell (5) */}
                <div className="rounded-xl border border-slate-200 p-4 bg-white space-y-3">
                  <div>
                    <h4 className="font-bold text-slate-800">Gradientes do topo das 5 quadrículas (Carta Roleta)</h4>
                    <p className="text-xs text-slate-500 mt-0.5">Cor superior e inferior do gradiente em cada uma das 5 faixas verticais (cada uma representa um nível SUPERT). A altura é 16% do topo da quadrícula.</p>
                  </div>
                  {(() => {
                    const DEF = [
                      { from: "#FFEB3B", to: "#FFF59D" },
                      { from: "#FFC107", to: "#FF9800" },
                      { from: "#FF9800", to: "#E64A19" },
                      { from: "#F44336", to: "#FF9800" },
                      { from: "#E64A19", to: "#FFEB3B" },
                    ];
                    const grads = Array.isArray(form.visual.cycle_top_gradients) && form.visual.cycle_top_gradients.length === 5
                      ? form.visual.cycle_top_gradients
                      : DEF;
                    const setOne = (idx, patch) => {
                      const next = grads.map((g, i) => (i === idx ? { ...g, ...patch } : g));
                      setVisual({ cycle_top_gradients: next });
                    };
                    return (
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                        {grads.map((g, i) => (
                          <div key={`grad-level-${i}`} className="bg-slate-50 rounded-lg border border-slate-200 p-3 space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] font-bold uppercase text-slate-600">Nível {i + 1}</span>
                              <div
                                className="w-6 h-6 rounded border border-slate-300"
                                style={{ background: `linear-gradient(180deg, ${g.from} 0%, ${g.to} 100%)` }}
                              />
                            </div>
                            <div>
                              <label className="text-[10px] font-bold text-slate-500 block">Topo</label>
                              <div className="flex items-center gap-1">
                                <input
                                  type="color"
                                  data-testid={`grad-${i + 1}-from`}
                                  value={g.from || "#FBBF24"}
                                  onChange={(e) => setOne(i, { from: e.target.value })}
                                  className="w-8 h-8 rounded border border-slate-300 cursor-pointer"
                                />
                                <input
                                  type="text"
                                  value={g.from || ""}
                                  onChange={(e) => setOne(i, { from: e.target.value })}
                                  className="flex-1 min-w-0 border border-slate-300 rounded px-1.5 py-1 text-[10px] font-mono"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="text-[10px] font-bold text-slate-500 block">Fundo</label>
                              <div className="flex items-center gap-1">
                                <input
                                  type="color"
                                  data-testid={`grad-${i + 1}-to`}
                                  value={g.to || "#F59E0B"}
                                  onChange={(e) => setOne(i, { to: e.target.value })}
                                  className="w-8 h-8 rounded border border-slate-300 cursor-pointer"
                                />
                                <input
                                  type="text"
                                  value={g.to || ""}
                                  onChange={(e) => setOne(i, { to: e.target.value })}
                                  className="flex-1 min-w-0 border border-slate-300 rounded px-1.5 py-1 text-[10px] font-mono"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setVisual({ cycle_top_gradients: [
                        { from: "#FFEB3B", to: "#FFF59D" },
                        { from: "#FFC107", to: "#FF9800" },
                        { from: "#FF9800", to: "#E64A19" },
                        { from: "#F44336", to: "#FF9800" },
                        { from: "#E64A19", to: "#FFEB3B" },
                      ] })}
                      data-testid="grad-reset"
                      className="text-[11px] font-bold text-slate-500 hover:text-slate-700 underline"
                    >Repor (Amarelo → Vermelho)</button>
                    <button
                      type="button"
                      onClick={() => setVisual({ cycle_top_gradients: [
                        { from: "#86efac", to: "#22c55e" },
                        { from: "#67e8f9", to: "#06b6d4" },
                        { from: "#a5b4fc", to: "#6366f1" },
                        { from: "#f9a8d4", to: "#ec4899" },
                        { from: "#fca5a5", to: "#ef4444" },
                      ] })}
                      data-testid="grad-preset-rainbow"
                      className="text-[11px] font-bold text-sky-600 hover:text-sky-800 underline"
                    >Predefinido: Arco-íris pastel</button>
                    <button
                      type="button"
                      onClick={() => setVisual({ cycle_top_gradients: [
                        { from: "#e2e8f0", to: "#cbd5e1" },
                        { from: "#cbd5e1", to: "#94a3b8" },
                        { from: "#94a3b8", to: "#64748b" },
                        { from: "#64748b", to: "#475569" },
                        { from: "#334155", to: "#0f172a" },
                      ] })}
                      data-testid="grad-preset-mono"
                      className="text-[11px] font-bold text-sky-600 hover:text-sky-800 underline"
                    >Predefinido: Monocromático</button>
                  </div>
                </div>

                {/* Indicador da quadrícula ativa — pisca sobre a zona branca */}
                <div className="rounded-xl border border-slate-200 p-4 bg-white space-y-3">
                  <div>
                    <h4 className="font-bold text-slate-800">Indicador da quadrícula ativa (Carta Roleta)</h4>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Cor a piscar sobre a <b>zona branca</b> da quadrícula do nível atual do jogador (não afeta o gradiente do topo). Fica ativo apenas quando o utilizador está a jogar essa posição e a roleta está parada.
                    </p>
                  </div>
                  <label className="flex items-center gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      data-testid="active-indicator-blink"
                      checked={form.visual.active_indicator_blink !== false}
                      onChange={(e) => setVisual({ active_indicator_blink: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-bold text-slate-700">Ativar animação de pisca-pisca</span>
                  </label>
                  <div>
                    <Label className="text-xs font-bold text-slate-600">Cor do pisca-pisca</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        data-testid="active-indicator-color"
                        value={form.visual.active_indicator_color || "#FCD34D"}
                        onChange={(e) => setVisual({ active_indicator_color: e.target.value })}
                        className="h-10 w-14 rounded border"
                      />
                      <Input
                        value={form.visual.active_indicator_color || "#FCD34D"}
                        onChange={(e) => setVisual({ active_indicator_color: e.target.value })}
                        className="flex-1"
                      />
                      <button
                        type="button"
                        onClick={() => setVisual({ active_indicator_color: "#FCD34D" })}
                        className="text-[11px] font-bold text-slate-500 hover:text-slate-700 underline"
                        data-testid="active-indicator-color-reset"
                      >Repor</button>
                    </div>
                  </div>
                </div>

                {/* Mini-card effects (SUPERT bar) */}
                <div className="rounded-xl border border-slate-200 p-4 bg-white space-y-3">
                  <div>
                    <h4 className="font-bold text-slate-800">Efeitos nas mini-cartas (SUPERT bar)</h4>
                    <p className="text-xs text-slate-500 mt-0.5">Animações aplicadas às cartas ganhas que aparecem na SUPERT bar.</p>
                  </div>
                  <label className="flex items-center gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      data-testid="game-supert-shimmer"
                      checked={!!form.visual.supert_card_shimmer}
                      onChange={(e) => setVisual({ supert_card_shimmer: e.target.checked })}
                      className="w-4 h-4 accent-amber-500"
                    />
                    <span className="text-sm font-semibold text-slate-700">✨ Shimmer (brilho a passar)</span>
                    <span className="text-xs text-slate-500">Faz passar um reflexo diagonal contínuo sobre as cartas ganhas.</span>
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-xl border border-slate-200 p-4 bg-white space-y-3">
                    <h4 className="font-bold text-slate-800">Carta Roleta (esquerda)</h4>
                    <div>
                      <Label>Inclinação Y (3D): <span className="font-mono">{form.visual.roulette_tilt_y ?? 10}°</span></Label>
                      <input
                        type="range"
                        min="-30"
                        max="30"
                        step="1"
                        value={form.visual.roulette_tilt_y ?? 10}
                        onChange={(e) => setVisual({ roulette_tilt_y: +e.target.value })}
                        className="w-full"
                        data-testid="visual-roulette-tilt-y"
                      />
                      <div className="text-[11px] text-slate-500 mt-1">Positivo = aresta esquerda recua (carta vira-se para a direita).</div>
                    </div>
                    <div>
                      <Label>Inclinação X (3D): <span className="font-mono">{form.visual.roulette_tilt_x ?? 0}°</span></Label>
                      <input
                        type="range"
                        min="-30"
                        max="30"
                        step="1"
                        value={form.visual.roulette_tilt_x ?? 0}
                        onChange={(e) => setVisual({ roulette_tilt_x: +e.target.value })}
                        className="w-full"
                        data-testid="visual-roulette-tilt-x"
                      />
                      <div className="text-[11px] text-slate-500 mt-1">Positivo = topo recua (inclina para trás); negativo = topo aproxima (inclina para a frente).</div>
                    </div>
                    <div>
                      <Label>Rotação Z (2D): <span className="font-mono">{form.visual.roulette_tilt_z ?? -4}°</span></Label>
                      <input
                        type="range"
                        min="-30"
                        max="30"
                        step="1"
                        value={form.visual.roulette_tilt_z ?? -4}
                        onChange={(e) => setVisual({ roulette_tilt_z: +e.target.value })}
                        className="w-full"
                        data-testid="visual-roulette-tilt-z"
                      />
                      <div className="text-[11px] text-slate-500 mt-1">Roda a carta no plano do ecrã (sentido horário se positivo).</div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 p-4 bg-white space-y-3">
                    <h4 className="font-bold text-slate-800">Carta Perguntas (direita)</h4>
                    <div>
                      <Label>Inclinação Y (3D): <span className="font-mono">{form.visual.card_front_tilt_y ?? -10}°</span></Label>
                      <input
                        type="range"
                        min="-30"
                        max="30"
                        step="1"
                        value={form.visual.card_front_tilt_y ?? -10}
                        onChange={(e) => setVisual({ card_front_tilt_y: +e.target.value })}
                        className="w-full"
                        data-testid="visual-cardfront-tilt-y"
                      />
                      <div className="text-[11px] text-slate-500 mt-1">Negativo = aresta direita recua (carta vira-se para a esquerda).</div>
                    </div>
                    <div>
                      <Label>Inclinação X (3D): <span className="font-mono">{form.visual.card_front_tilt_x ?? 0}°</span></Label>
                      <input
                        type="range"
                        min="-30"
                        max="30"
                        step="1"
                        value={form.visual.card_front_tilt_x ?? 0}
                        onChange={(e) => setVisual({ card_front_tilt_x: +e.target.value })}
                        className="w-full"
                        data-testid="visual-cardfront-tilt-x"
                      />
                      <div className="text-[11px] text-slate-500 mt-1">Positivo = topo recua (inclina para trás); negativo = topo aproxima (inclina para a frente).</div>
                    </div>
                    <div>
                      <Label>Rotação Z (2D): <span className="font-mono">{form.visual.card_front_tilt_z ?? 0}°</span></Label>
                      <input
                        type="range"
                        min="-30"
                        max="30"
                        step="1"
                        value={form.visual.card_front_tilt_z ?? 0}
                        onChange={(e) => setVisual({ card_front_tilt_z: +e.target.value })}
                        className="w-full"
                        data-testid="visual-cardfront-tilt-z"
                      />
                      <div className="text-[11px] text-slate-500 mt-1">Roda a carta no plano do ecrã (sentido horário se positivo).</div>
                    </div>
                  </div>
                </div>

                {/* Zoom & posicionamento (independente por carta) */}
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-1">Zoom & Posição</div>
                  <p className="text-xs text-slate-500 mb-3">Ajusta o tamanho e a posição de cada carta no ecrã, independentemente. Útil para alinhar visualmente sem mudar o layout.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Roulette */}
                    <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-3">
                      <h5 className="text-xs font-bold text-slate-700">Carta Roleta (esquerda)</h5>
                      <div>
                        <Label>Zoom: <span className="font-mono">{(form.visual.roulette_scale ?? 1).toFixed(2)}×</span></Label>
                        <input
                          type="range"
                          min="0.5"
                          max="2"
                          step="0.05"
                          value={form.visual.roulette_scale ?? 1}
                          onChange={(e) => setVisual({ roulette_scale: parseFloat(e.target.value) })}
                          className="w-full"
                          data-testid="visual-roulette-scale"
                        />
                        <div className="text-[11px] text-slate-500 mt-1">0.50× a 2.00× (1.00× = tamanho original).</div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label>Posição X: <span className="font-mono">{form.visual.roulette_offset_x ?? 0}px</span></Label>
                          <input
                            type="range"
                            min="-200"
                            max="200"
                            step="2"
                            value={form.visual.roulette_offset_x ?? 0}
                            onChange={(e) => setVisual({ roulette_offset_x: +e.target.value })}
                            className="w-full"
                            data-testid="visual-roulette-offset-x"
                          />
                        </div>
                        <div>
                          <Label>Posição Y: <span className="font-mono">{form.visual.roulette_offset_y ?? 0}px</span></Label>
                          <input
                            type="range"
                            min="-200"
                            max="200"
                            step="2"
                            value={form.visual.roulette_offset_y ?? 0}
                            onChange={(e) => setVisual({ roulette_offset_y: +e.target.value })}
                            className="w-full"
                            data-testid="visual-roulette-offset-y"
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        className="text-[11px] text-sky-700 hover:text-sky-900 font-bold underline"
                        onClick={() => setVisual({ roulette_scale: 1, roulette_offset_x: 0, roulette_offset_y: 0 })}
                        data-testid="btn-reset-roulette-transform"
                      >
                        Repor (1×, 0, 0)
                      </button>
                    </div>

                    {/* Card Front */}
                    <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-3">
                      <h5 className="text-xs font-bold text-slate-700">Carta Perguntas (direita)</h5>
                      <div>
                        <Label>Zoom: <span className="font-mono">{(form.visual.card_front_scale ?? 1).toFixed(2)}×</span></Label>
                        <input
                          type="range"
                          min="0.5"
                          max="2"
                          step="0.05"
                          value={form.visual.card_front_scale ?? 1}
                          onChange={(e) => setVisual({ card_front_scale: parseFloat(e.target.value) })}
                          className="w-full"
                          data-testid="visual-cardfront-scale"
                        />
                        <div className="text-[11px] text-slate-500 mt-1">0.50× a 2.00× (1.00× = tamanho original).</div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label>Posição X: <span className="font-mono">{form.visual.card_front_offset_x ?? 0}px</span></Label>
                          <input
                            type="range"
                            min="-200"
                            max="200"
                            step="2"
                            value={form.visual.card_front_offset_x ?? 0}
                            onChange={(e) => setVisual({ card_front_offset_x: +e.target.value })}
                            className="w-full"
                            data-testid="visual-cardfront-offset-x"
                          />
                        </div>
                        <div>
                          <Label>Posição Y: <span className="font-mono">{form.visual.card_front_offset_y ?? 0}px</span></Label>
                          <input
                            type="range"
                            min="-200"
                            max="200"
                            step="2"
                            value={form.visual.card_front_offset_y ?? 0}
                            onChange={(e) => setVisual({ card_front_offset_y: +e.target.value })}
                            className="w-full"
                            data-testid="visual-cardfront-offset-y"
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        className="text-[11px] text-sky-700 hover:text-sky-900 font-bold underline"
                        onClick={() => setVisual({ card_front_scale: 1, card_front_offset_x: 0, card_front_offset_y: 0 })}
                        data-testid="btn-reset-cardfront-transform"
                      >
                        Repor (1×, 0, 0)
                      </button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Intensidade da perspetiva: <span className="font-mono">{form.visual.perspective_strength ?? 1400}px</span></Label>
                    <input
                      type="range"
                      min="600"
                      max="3000"
                      step="50"
                      value={form.visual.perspective_strength ?? 1400}
                      onChange={(e) => setVisual({ perspective_strength: +e.target.value })}
                      className="w-full"
                      data-testid="visual-perspective-strength"
                    />
                    <div className="text-[11px] text-slate-500 mt-1">Menor = efeito mais dramático (cartas parecem mais perto da câmara).</div>
                  </div>

                  <div className="flex items-end gap-2">
                    <Switch
                      checked={form.visual.card_depth_shadow !== false}
                      onCheckedChange={(v) => setVisual({ card_depth_shadow: v })}
                      data-testid="visual-card-depth-shadow"
                    />
                    <Label className="mb-1">
                      Sombra de profundidade {form.visual.card_depth_shadow === false ? "(desligada)" : "(ligada)"}
                    </Label>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    data-testid="btn-tilt-preset-default"
                    onClick={() => setVisual({ roulette_tilt_y: 10, card_front_tilt_y: -10, roulette_tilt_x: 0, card_front_tilt_x: 0, roulette_tilt_z: -4, card_front_tilt_z: 0, perspective_strength: 1400, card_depth_shadow: true, roulette_scale: 1, roulette_offset_x: 0, roulette_offset_y: 0, card_front_scale: 1, card_front_offset_x: 0, card_front_offset_y: 0 })}
                  >
                    Predefinido (Open Book)
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    data-testid="btn-tilt-preset-flat"
                    onClick={() => setVisual({ roulette_tilt_y: 0, card_front_tilt_y: 0, roulette_tilt_x: 0, card_front_tilt_x: 0, roulette_tilt_z: 0, card_front_tilt_z: 0, perspective_strength: 2400, card_depth_shadow: false, roulette_scale: 1, roulette_offset_x: 0, roulette_offset_y: 0, card_front_scale: 1, card_front_offset_x: 0, card_front_offset_y: 0 })}
                  >
                    Plano (sem inclinação)
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    data-testid="btn-tilt-preset-dramatic"
                    onClick={() => setVisual({ roulette_tilt_y: 18, card_front_tilt_y: -18, roulette_tilt_x: -8, card_front_tilt_x: -8, roulette_tilt_z: -6, card_front_tilt_z: 6, perspective_strength: 900, card_depth_shadow: true })}
                  >
                    Dramático
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    data-testid="btn-tilt-preset-subtle"
                    onClick={() => setVisual({ roulette_tilt_y: 5, card_front_tilt_y: -5, roulette_tilt_x: 0, card_front_tilt_x: 0, roulette_tilt_z: -2, card_front_tilt_z: 0, perspective_strength: 2000, card_depth_shadow: true })}
                  >
                    Subtil
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    className="ml-auto bg-sky-500 hover:bg-sky-600 text-white"
                    data-testid="btn-preview-perspective"
                    onClick={() => setShowPerspectivePreview((v) => !v)}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    {showPerspectivePreview ? "Esconder previsão" : "Previsualizar em jogo"}
                  </Button>
                </div>

                {showPerspectivePreview && (
                  <PerspectivePreview visual={form.visual} />
                )}
              </div>
            )}


            {tab === "card_logo" && (
              <div className="space-y-4">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-1">Logotipo nas cartas do jogo</div>
                  <p className="text-xs text-slate-500 mb-3">
                    Imagem exibida no canto inferior direito das cartas deste jogo (Carta Perguntas e Carta Roleta). Se não tiver imagem, <b>nada é mostrado</b> — o logotipo é agora exclusivamente por-jogo.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Upload / preview */}
                    <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-3">
                      <Label className="text-xs">Imagem</Label>
                      {form.visual.card_logo_image ? (
                        <div className="flex items-start gap-3">
                          <div className="w-24 h-24 rounded-md bg-slate-100 border border-slate-200 grid place-items-center overflow-hidden">
                            <img
                              src={form.visual.card_logo_image}
                              alt="preview"
                              className="max-w-full max-h-full object-contain"
                              data-testid="game-card-logo-preview"
                            />
                          </div>
                          <div className="flex flex-col gap-2">
                            <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-100 hover:bg-sky-200 text-sky-800 text-xs font-bold cursor-pointer">
                              <input type="file" accept="image/*" className="hidden" data-testid="game-card-logo-upload" onChange={(e) => onUploadCardLogo(e.target.files?.[0])} />
                              Substituir
                            </label>
                            <button
                              type="button"
                              className="text-[11px] text-rose-600 hover:text-rose-800 font-bold underline text-left"
                              onClick={() => setVisual({ card_logo_image: null })}
                              data-testid="btn-clear-card-logo"
                            >
                              Remover (sem logotipo)
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start gap-3">
                          <div className="w-24 h-24 rounded-md bg-slate-100 border border-dashed border-slate-300 grid place-items-center text-[10px] text-slate-500 text-center px-2">
                            Sem logotipo
                          </div>
                          <div className="flex flex-col gap-2">
                            <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-100 hover:bg-sky-200 text-sky-800 text-xs font-bold cursor-pointer">
                              <input type="file" accept="image/*" className="hidden" data-testid="game-card-logo-upload" onChange={(e) => onUploadCardLogo(e.target.files?.[0])} />
                              Enviar imagem
                            </label>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Filtros de cor */}
                    <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-3">
                      <Label className="text-xs">Ajustes de cor</Label>
                      <div>
                        <Label className="text-xs">Hue: <span className="font-mono">{form.visual.card_logo_hue ?? 0}°</span></Label>
                        <input type="range" min="0" max="360" step="1" value={form.visual.card_logo_hue ?? 0} onChange={(e) => setVisual({ card_logo_hue: +e.target.value })} className="w-full" />
                      </div>
                      <div>
                        <Label className="text-xs">Saturação: <span className="font-mono">{(form.visual.card_logo_saturate ?? 1).toFixed(2)}</span></Label>
                        <input type="range" min="0" max="3" step="0.05" value={form.visual.card_logo_saturate ?? 1} onChange={(e) => setVisual({ card_logo_saturate: parseFloat(e.target.value) })} className="w-full" />
                      </div>
                      <div>
                        <Label className="text-xs">Brilho: <span className="font-mono">{(form.visual.card_logo_brightness ?? 1).toFixed(2)}</span></Label>
                        <input type="range" min="0" max="3" step="0.05" value={form.visual.card_logo_brightness ?? 1} onChange={(e) => setVisual({ card_logo_brightness: parseFloat(e.target.value) })} className="w-full" />
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={form.visual.card_logo_invert === true}
                          onCheckedChange={(v) => setVisual({ card_logo_invert: v })}
                        />
                        <Label className="text-xs">Inverter cores</Label>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    {/* Carta Perguntas */}
                    <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <h5 className="text-xs font-bold text-slate-700">Carta Perguntas</h5>
                        <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="toggle-card-logo-front">
                          <input
                            type="checkbox"
                            className="peer sr-only"
                            checked={form.visual.card_logo_front_enabled !== false}
                            onChange={(e) => setVisual({ card_logo_front_enabled: e.target.checked })}
                          />
                          <span className="relative inline-flex h-5 w-9 items-center rounded-full bg-slate-300 peer-checked:bg-emerald-500 transition">
                            <span className="inline-block h-4 w-4 transform rounded-full bg-white shadow transition translate-x-0.5 peer-checked:translate-x-[18px]" />
                          </span>
                          <span className="text-[11px] text-slate-600 font-medium">{form.visual.card_logo_front_enabled !== false ? "Visível" : "Oculto"}</span>
                        </label>
                      </div>
                      <div>
                        <Label className="text-xs">Altura (px): <span className="font-mono">{form.visual.card_logo_front_height ?? 44}</span></Label>
                        <input type="range" min="10" max="200" step="1" value={form.visual.card_logo_front_height ?? 44} onChange={(e) => setVisual({ card_logo_front_height: +e.target.value })} className="w-full" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Offset X: <span className="font-mono">{form.visual.card_logo_front_offset_x ?? -10}px</span></Label>
                          <input type="range" min="-200" max="200" step="1" value={form.visual.card_logo_front_offset_x ?? -10} onChange={(e) => setVisual({ card_logo_front_offset_x: +e.target.value })} className="w-full" />
                        </div>
                        <div>
                          <Label className="text-xs">Offset Y: <span className="font-mono">{form.visual.card_logo_front_offset_y ?? 10}px</span></Label>
                          <input type="range" min="-200" max="200" step="1" value={form.visual.card_logo_front_offset_y ?? 10} onChange={(e) => setVisual({ card_logo_front_offset_y: +e.target.value })} className="w-full" />
                        </div>
                      </div>
                    </div>

                    {/* Carta Roleta */}
                    <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <h5 className="text-xs font-bold text-slate-700">Carta Roleta</h5>
                        <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="toggle-card-logo-roulette">
                          <input
                            type="checkbox"
                            className="peer sr-only"
                            checked={form.visual.card_logo_roulette_enabled !== false}
                            onChange={(e) => setVisual({ card_logo_roulette_enabled: e.target.checked })}
                          />
                          <span className="relative inline-flex h-5 w-9 items-center rounded-full bg-slate-300 peer-checked:bg-emerald-500 transition">
                            <span className="inline-block h-4 w-4 transform rounded-full bg-white shadow transition translate-x-0.5 peer-checked:translate-x-[18px]" />
                          </span>
                          <span className="text-[11px] text-slate-600 font-medium">{form.visual.card_logo_roulette_enabled !== false ? "Visível" : "Oculto"}</span>
                        </label>
                      </div>
                      <div>
                        <Label className="text-xs">Altura (px): <span className="font-mono">{form.visual.card_logo_roulette_height ?? 44}</span></Label>
                        <input type="range" min="10" max="200" step="1" value={form.visual.card_logo_roulette_height ?? 44} onChange={(e) => setVisual({ card_logo_roulette_height: +e.target.value })} className="w-full" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Offset X: <span className="font-mono">{form.visual.card_logo_roulette_offset_x ?? -10}px</span></Label>
                          <input type="range" min="-200" max="200" step="1" value={form.visual.card_logo_roulette_offset_x ?? -10} onChange={(e) => setVisual({ card_logo_roulette_offset_x: +e.target.value })} className="w-full" />
                        </div>
                        <div>
                          <Label className="text-xs">Offset Y: <span className="font-mono">{form.visual.card_logo_roulette_offset_y ?? 10}px</span></Label>
                          <input type="range" min="-200" max="200" step="1" value={form.visual.card_logo_roulette_offset_y ?? 10} onChange={(e) => setVisual({ card_logo_roulette_offset_y: +e.target.value })} className="w-full" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* LIVE PREVIEW — two card mock-ups mirroring the real
                      GamePlay layout. Logo image is positioned with the same
                      right/bottom math as RouletteCard / CardFront. All
                      edits update immediately so admins can nail the
                      offsets without reloading a real game. */}
                  {form.visual.card_logo_image && (
                    <div className="mt-4 rounded-lg border border-sky-200 bg-sky-50 p-3">
                      <div className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <span>Pré-visualização ao vivo</span>
                        <span className="text-[10px] font-normal normal-case text-slate-500">(reflete os ajustes acima)</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <CardLogoPreview
                          label="Carta Perguntas"
                          image={form.visual.card_logo_image}
                          enabled={form.visual.card_logo_front_enabled !== false}
                          height={form.visual.card_logo_front_height ?? 44}
                          offsetX={form.visual.card_logo_front_offset_x ?? -10}
                          offsetY={form.visual.card_logo_front_offset_y ?? 10}
                          hue={form.visual.card_logo_hue ?? 0}
                          saturate={form.visual.card_logo_saturate ?? 1}
                          brightness={form.visual.card_logo_brightness ?? 1}
                          invert={form.visual.card_logo_invert === true}
                          variant="front"
                        />
                        <CardLogoPreview
                          label="Carta Roleta"
                          image={form.visual.card_logo_image}
                          enabled={form.visual.card_logo_roulette_enabled !== false}
                          height={form.visual.card_logo_roulette_height ?? 44}
                          offsetX={form.visual.card_logo_roulette_offset_x ?? -10}
                          offsetY={form.visual.card_logo_roulette_offset_y ?? 10}
                          hue={form.visual.card_logo_hue ?? 0}
                          saturate={form.visual.card_logo_saturate ?? 1}
                          brightness={form.visual.card_logo_brightness ?? 1}
                          invert={form.visual.card_logo_invert === true}
                          variant="roulette"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}


            {tab === "fx" && (
              <div className="space-y-3">
                <div>
                  <Label>Efeito de entrada da carta pergunta</Label>
                  <Select value={form.card_effect} onValueChange={(v) => setForm({ ...form, card_effect: v })}>
                    <SelectTrigger data-testid="game-card-effect"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CARD_EFFECTS.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Efeito de entrada da carta roleta</Label>
                  <Select value={form.roulette_effect} onValueChange={(v) => setForm({ ...form, roulette_effect: v })}>
                    <SelectTrigger data-testid="game-roulette-effect"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROULETTE_EFFECTS.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Som ao surgir nova carta</Label>
                  <Select value={form.card_sound} onValueChange={(v) => setForm({ ...form, card_sound: v })}>
                    <SelectTrigger data-testid="game-card-sound"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SOUND_OPTIONS.map((e) => <SelectItem key={e.value} value={e.value} data-testid={`game-card-sound-${e.value}`}>{e.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="mt-3">
                    <Label className="text-xs">
                      Volume: <span className="font-mono">{Math.round((form.card_sound_volume ?? 0.7) * 100)}%</span>
                    </Label>
                    <div className="flex items-center gap-3 mt-1">
                      <input type="range" min="0" max="1" step="0.05" value={form.card_sound_volume ?? 0.7}
                        onChange={(e) => setForm({ ...form, card_sound_volume: +e.target.value })}
                        className="flex-1" data-testid="game-card-sound-volume" />
                      <button type="button" data-testid="game-card-sound-preview"
                        onClick={() => previewSound(form.card_sound || "deal", form.card_sound_volume ?? 0.7)}
                        disabled={!form.card_sound || form.card_sound === "none"}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-sky-100 hover:bg-sky-200 text-sky-800 text-xs font-bold disabled:opacity-40">
                        <Volume2 className="w-3.5 h-3.5" /> Testar
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Som ao acertar a resposta</Label>
                  <Select value={form.correct_sound ?? "correct"} onValueChange={(v) => setForm({ ...form, correct_sound: v })}>
                    <SelectTrigger data-testid="game-correct-sound"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SOUND_OPTIONS.map((e) => <SelectItem key={e.value} value={e.value} data-testid={`game-correct-sound-${e.value}`}>{e.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="mt-3">
                    <Label className="text-xs">
                      Volume: <span className="font-mono">{Math.round((form.correct_sound_volume ?? 0.7) * 100)}%</span>
                    </Label>
                    <div className="flex items-center gap-3 mt-1">
                      <input type="range" min="0" max="1" step="0.05" value={form.correct_sound_volume ?? 0.7}
                        onChange={(e) => setForm({ ...form, correct_sound_volume: +e.target.value })}
                        className="flex-1" data-testid="game-correct-sound-volume" />
                      <button type="button" data-testid="game-correct-sound-preview"
                        onClick={() => previewSound(form.correct_sound || "correct", form.correct_sound_volume ?? 0.7)}
                        disabled={form.correct_sound === "none"}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-100 hover:bg-emerald-200 text-emerald-800 text-xs font-bold disabled:opacity-40">
                        <Volume2 className="w-3.5 h-3.5" /> Testar
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Som ao errar a resposta</Label>
                  <Select value={form.wrong_sound ?? "wrong"} onValueChange={(v) => setForm({ ...form, wrong_sound: v })}>
                    <SelectTrigger data-testid="game-wrong-sound"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SOUND_OPTIONS.map((e) => <SelectItem key={e.value} value={e.value} data-testid={`game-wrong-sound-${e.value}`}>{e.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="mt-3">
                    <Label className="text-xs">
                      Volume: <span className="font-mono">{Math.round((form.wrong_sound_volume ?? 0.7) * 100)}%</span>
                    </Label>
                    <div className="flex items-center gap-3 mt-1">
                      <input type="range" min="0" max="1" step="0.05" value={form.wrong_sound_volume ?? 0.7}
                        onChange={(e) => setForm({ ...form, wrong_sound_volume: +e.target.value })}
                        className="flex-1" data-testid="game-wrong-sound-volume" />
                      <button type="button" data-testid="game-wrong-sound-preview"
                        onClick={() => previewSound(form.wrong_sound || "wrong", form.wrong_sound_volume ?? 0.7)}
                        disabled={form.wrong_sound === "none"}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-rose-100 hover:bg-rose-200 text-rose-800 text-xs font-bold disabled:opacity-40">
                        <Volume2 className="w-3.5 h-3.5" /> Testar
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Som de vitória (fim de jogo — ganhou)</Label>
                  <Select value={form.victory_sound ?? "victory"} onValueChange={(v) => setForm({ ...form, victory_sound: v })}>
                    <SelectTrigger data-testid="game-victory-sound"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SOUND_OPTIONS.map((e) => <SelectItem key={e.value} value={e.value} data-testid={`game-victory-sound-${e.value}`}>{e.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="mt-3">
                    <Label className="text-xs">
                      Volume: <span className="font-mono">{Math.round((form.victory_sound_volume ?? 0.8) * 100)}%</span>
                    </Label>
                    <div className="flex items-center gap-3 mt-1">
                      <input type="range" min="0" max="1" step="0.05" value={form.victory_sound_volume ?? 0.8}
                        onChange={(e) => setForm({ ...form, victory_sound_volume: +e.target.value })}
                        className="flex-1" data-testid="game-victory-sound-volume" />
                      <button type="button" data-testid="game-victory-sound-preview"
                        onClick={() => previewSound(form.victory_sound || "victory", form.victory_sound_volume ?? 0.8)}
                        disabled={form.victory_sound === "none"}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-100 hover:bg-emerald-200 text-emerald-800 text-xs font-bold disabled:opacity-40">
                        <Volume2 className="w-3.5 h-3.5" /> Testar
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Som de derrota (fim de jogo — perdeu)</Label>
                  <Select value={form.defeat_sound ?? "defeat"} onValueChange={(v) => setForm({ ...form, defeat_sound: v })}>
                    <SelectTrigger data-testid="game-defeat-sound"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SOUND_OPTIONS.map((e) => <SelectItem key={e.value} value={e.value} data-testid={`game-defeat-sound-${e.value}`}>{e.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="mt-3">
                    <Label className="text-xs">
                      Volume: <span className="font-mono">{Math.round((form.defeat_sound_volume ?? 0.7) * 100)}%</span>
                    </Label>
                    <div className="flex items-center gap-3 mt-1">
                      <input type="range" min="0" max="1" step="0.05" value={form.defeat_sound_volume ?? 0.7}
                        onChange={(e) => setForm({ ...form, defeat_sound_volume: +e.target.value })}
                        className="flex-1" data-testid="game-defeat-sound-volume" />
                      <button type="button" data-testid="game-defeat-sound-preview"
                        onClick={() => previewSound(form.defeat_sound || "defeat", form.defeat_sound_volume ?? 0.7)}
                        disabled={form.defeat_sound === "none"}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold disabled:opacity-40">
                        <Volume2 className="w-3.5 h-3.5" /> Testar
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Som ao sair letra repetida (oops/rrrr)</Label>
                  <Select value={form.repeat_sound ?? "oops"} onValueChange={(v) => setForm({ ...form, repeat_sound: v })}>
                    <SelectTrigger data-testid="game-repeat-sound"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SOUND_OPTIONS.map((e) => <SelectItem key={e.value} value={e.value} data-testid={`game-repeat-sound-${e.value}`}>{e.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="text-[11px] text-slate-500 mt-1">Tocado quando o jogador acerta mas a letra atribuída já tinha sido conquistada (apenas no modo normal).</div>
                  <div className="mt-3">
                    <Label className="text-xs">
                      Volume: <span className="font-mono">{Math.round((form.repeat_sound_volume ?? 0.7) * 100)}%</span>
                    </Label>
                    <div className="flex items-center gap-3 mt-1">
                      <input type="range" min="0" max="1" step="0.05" value={form.repeat_sound_volume ?? 0.7}
                        onChange={(e) => setForm({ ...form, repeat_sound_volume: +e.target.value })}
                        className="flex-1" data-testid="game-repeat-sound-volume" />
                      <button type="button" data-testid="game-repeat-sound-preview"
                        onClick={() => previewSound(form.repeat_sound || "oops", form.repeat_sound_volume ?? 0.7)}
                        disabled={form.repeat_sound === "none"}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-bold disabled:opacity-40">
                        <Volume2 className="w-3.5 h-3.5" /> Testar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <Button data-testid="btn-save-game" onClick={save} className="w-full bg-slate-900 mt-2">{t("save")}</Button>
          </DialogContent>
        </Dialog>
      </div>

      {/* PREVIEW MODAL */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Pré-visualização do ecrã de jogo</DialogTitle>
            <DialogDescription className="sr-only">Pré-visualização interativa do ecrã de jogo com as configurações actuais aplicadas.</DialogDescription>
          </DialogHeader>
          <GamePreview visual={form.visual} gameName={form.name || "Novo Jogo"} />
        </DialogContent>
      </Dialog>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50/60">
          <Button
            data-testid="btn-games-delete-selected"
            disabled={!selected.size}
            onClick={deleteSelected}
            variant="outline"
            size="sm"
            className="text-rose-700 border-rose-300 hover:bg-rose-50 disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4 mr-1" /> Apagar selecionados ({selected.size})
          </Button>
          <Button
            data-testid="btn-games-delete-all"
            disabled={!games.length}
            onClick={deleteAll}
            variant="outline"
            size="sm"
            className="text-rose-700 border-rose-400 hover:bg-rose-50 disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4 mr-1" /> Apagar todos
          </Button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-3 py-3 w-8">
                <input
                  type="checkbox"
                  data-testid="games-select-all"
                  className="w-4 h-4 cursor-pointer"
                  checked={games.length > 0 && selected.size === games.length}
                  onChange={toggleSelectAll}
                />
              </th>
              <th className="text-left px-4 py-3 font-bold text-slate-700 text-xs uppercase">Nome</th>
              <th className="text-left px-4 py-3 font-bold text-slate-700 text-xs uppercase">Status</th>
              <th className="text-left px-4 py-3 font-bold text-slate-700 text-xs uppercase">Modo</th>
              <th className="text-left px-4 py-3 font-bold text-slate-700 text-xs uppercase">SUPERT</th>
              <th className="text-left px-4 py-3 font-bold text-slate-700 text-xs uppercase">Tentativas</th>
              <th className="text-left px-4 py-3 font-bold text-slate-700 text-xs uppercase">Ativo</th>
              <th className="text-left px-4 py-3 font-bold text-slate-700 text-xs uppercase">Ações</th>
            </tr>
          </thead>
          <tbody data-testid="games-table">
            {games.map((g) => {
              const isSel = selected.has(g.id);
              return (
              <tr key={g.id} className={`border-b hover:bg-slate-50 ${isSel ? "bg-rose-50" : ""}`}>
                <td className="px-3 py-3">
                  <input
                    type="checkbox"
                    data-testid={`game-select-${g.id}`}
                    className="w-4 h-4 cursor-pointer"
                    checked={isSel}
                    onChange={() => toggleSelect(g.id)}
                  />
                </td>
                <td className="px-4 py-3 font-medium">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span data-testid={`game-row-name-${g.id}`}>{displayName(g)}</span>
                    {missingTranslation(g) && (
                      <span
                        className="text-[10px] font-bold uppercase tracking-wider bg-rose-100 border border-rose-300 text-rose-700 rounded-full px-2 py-0.5"
                        title={`Este jogo não tem nome traduzido em ${(lang || "pt").toUpperCase()} — clica em editar para adicionar.`}
                        data-testid={`game-row-missing-${(lang || "pt")}-${g.id}`}
                      >
                        ⚠ sem tradução {(lang || "pt").toUpperCase()}
                      </span>
                    )}
                    {(() => {
                      const dups = duplicatedLangs(g);
                      if (dups.length === 0) return null;
                      return (
                        <span
                          className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 border border-amber-300 text-amber-800 rounded-full px-2 py-0.5"
                          title={`Nome duplicado em ${dups.join(", ").toUpperCase()}`}
                          data-testid={`game-row-dupe-badge-${g.id}`}
                        >
                          ⚠ dup. {dups.map((l) => l.toUpperCase()).join("/")}
                        </span>
                      );
                    })()}
                  </div>
                </td>
                <td className="px-4 py-3" data-testid={`game-row-status-${g.id}`}>
                  {g.status === "training"
                    ? t("status_training")
                    : g.status === "competition"
                    ? t("status_competition")
                    : g.status === "eliminatorias"
                    ? t("status_eliminatorias")
                    : g.status}
                </td>
                <td className="px-4 py-3">{g.mode}</td>
                <td className="px-4 py-3">{g.supert_count}</td>
                <td className="px-4 py-3">{g.max_attempts}</td>
                <td className="px-4 py-3" data-testid={`game-row-active-${g.id}`}>
                  {(() => {
                    // Show 3 mini pills (PT/EN/ES). Lazy migration: if a game
                    // has no `active_languages` map yet, derive from the legacy
                    // boolean so existing games keep showing as fully active.
                    const al = g.active_languages || {
                      pt: !!g.active, en: !!g.active, es: !!g.active,
                    };
                    return (
                      <div className="flex items-center gap-1">
                        {["pt", "en", "es"].map((l) => (
                          <span
                            key={l}
                            className={`px-1.5 py-0.5 rounded text-[10px] font-bold tabular-nums ${
                              al[l] ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400 line-through"
                            }`}
                            title={al[l] ? `Ativo em ${l.toUpperCase()}` : `Inativo em ${l.toUpperCase()}`}
                          >
                            {l.toUpperCase()}
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-4 py-3 flex gap-2">
                  <Link to={`/admin/perguntas/${g.id}`} className="px-2 py-1 text-xs bg-slate-100 rounded">Perguntas</Link>
                  <button
                    data-testid={`btn-edit-game-${g.id}`}
                    onClick={() => {
                      setEditing(g);
                      setForm({
                        name: g.name,
                        status: g.status,
                        mode: g.mode,
                        supert_count: g.supert_count,
                        max_attempts: g.max_attempts,
                        active: g.active,
                        // Lazy migration: legacy games without `active_languages`
                        // inherit the boolean `active` for every language so the
                        // UX is "fully active" by default after migration.
                        active_languages: g.active_languages || { pt: !!g.active, en: !!g.active, es: !!g.active },
                        translations: g.translations || {},
                        format: g.format || "original",
                        card_effect: g.card_effect || "fade",
                        roulette_effect: g.roulette_effect || "slide_left",
                        card_sound: g.card_sound || "deal",
                        card_sound_volume: g.card_sound_volume ?? 0.7,
                        correct_sound: g.correct_sound || "correct",
                        correct_sound_volume: g.correct_sound_volume ?? 0.7,
                        wrong_sound: g.wrong_sound || "wrong",
                        wrong_sound_volume: g.wrong_sound_volume ?? 0.7,
                        victory_sound: g.victory_sound || "victory",
                        victory_sound_volume: g.victory_sound_volume ?? 0.8,
                        defeat_sound: g.defeat_sound || "defeat",
                        defeat_sound_volume: g.defeat_sound_volume ?? 0.7,
                        repeat_sound: g.repeat_sound || "oops",
                        repeat_sound_volume: g.repeat_sound_volume ?? 0.7,
                        round_delay_ms: g.round_delay_ms ?? 100,
                        simultaneous_ms: g.simultaneous_ms ?? 500,
                        teacher_password: g.teacher_password || "",
                        level_system: g.level_system || "levels",
                        tiers: Array.isArray(g.tiers) ? g.tiers : [],
                        max_repeated_letters: g.max_repeated_letters ?? 0,
                        lives: g.lives ?? 1,
                        time_penalty_seconds: g.time_penalty_seconds ?? 5,
                        visual: { ...DEFAULT_VISUAL, ...(g.visual || {}) },
                      });
                      setTab("basic");
                      setOpen(true);
                    }}
                    className="text-slate-600 hover:text-sky-600"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => duplicate(g.id)} className="text-slate-600 hover:text-amber-600"><Copy className="w-4 h-4" /></button>
                  <button data-testid={`btn-delete-game-${g.id}`} onClick={() => remove(g.id)} className="text-slate-600 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// --- Game preview component (compact mock of the game screen) ---
function GamePreview({ visual, gameName }) {
  const bg = getBackgroundCss(visual);
  const textStyle = getQuestionTextStyle(visual);
  const sample = [
    { pos: "A", q: "8 + 7" }, { pos: "F", q: "5 × 6 + 9" },
    { pos: "B", q: "17 − 9" }, { pos: "G", q: "40 ÷ 8 + 11" },
    { pos: "C", q: "12 + 5 − 7" }, { pos: "H", q: "35 ÷ 7 + 9 − 3" },
    { pos: "D", q: "4 × 8" }, { pos: "I", q: "4 × 5 + 8 × 8" },
    { pos: "E", q: "30 ÷ 5" }, { pos: "J", q: "10 × 9 + 3 ÷ 1" },
  ];
  return (
    <div
      className="rounded-xl p-4 h-[420px] relative overflow-hidden"
      style={{ background: bg, backgroundSize: visual.bg_type === "image" ? "cover" : undefined }}
      data-testid="game-preview"
    >
      <div className="text-center text-white font-[Fredoka] font-bold drop-shadow mb-3 text-sm">
        {gameName}
      </div>
      <div className="mx-auto max-w-[420px] bg-[#0a1e3a] rounded-xl p-3 shadow-2xl">
        <div className="grid grid-cols-2 grid-rows-5 grid-flow-col gap-1.5">
          {sample.map((it) => (
            <div key={it.pos} className="flex items-center gap-1.5">
              <span className="font-[Fredoka] font-extrabold italic text-amber-400 text-lg" style={{ textShadow: "1.5px 1.5px 0 rgba(0,0,0,0.5)" }}>{it.pos}</span>
              <div className="flex-1 bg-white rounded-md border border-slate-300 px-2 py-1 truncate" style={textStyle}>
                {it.q}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 max-w-[420px] mx-auto">
        {["18", "19", "20", "Nenhuma"].map((o) => (
          <div key={o} className="bg-slate-300 text-slate-800 rounded-full py-2 text-center font-bold text-sm">{o}</div>
        ))}
      </div>
    </div>
  );
}

function PerspectivePreview({ visual }) {
  const tiltR = Number.isFinite(visual?.roulette_tilt_y) ? Number(visual.roulette_tilt_y) : 10;
  const tiltC = Number.isFinite(visual?.card_front_tilt_y) ? Number(visual.card_front_tilt_y) : -10;
  const tiltRx = Number.isFinite(visual?.roulette_tilt_x) ? Number(visual.roulette_tilt_x) : 0;
  const tiltCx = Number.isFinite(visual?.card_front_tilt_x) ? Number(visual.card_front_tilt_x) : 0;
  const tiltRz = Number.isFinite(visual?.roulette_tilt_z) ? Number(visual.roulette_tilt_z) : -4;
  const tiltCz = Number.isFinite(visual?.card_front_tilt_z) ? Number(visual.card_front_tilt_z) : 0;
  const persp = Math.max(400, Math.min(4000, Number(visual?.perspective_strength) || 1400));
  const shadow = visual?.card_depth_shadow !== false;
  // Live transforms (scale + offset) — preview uses 25% of the offset so the panel doesn't overflow.
  const scaleR = Math.max(0.5, Math.min(2, Number(visual?.roulette_scale) || 1));
  const scaleC = Math.max(0.5, Math.min(2, Number(visual?.card_front_scale) || 1));
  const offRx = (Number(visual?.roulette_offset_x) || 0) * 0.25;
  const offRy = (Number(visual?.roulette_offset_y) || 0) * 0.25;
  const offCx = (Number(visual?.card_front_offset_x) || 0) * 0.25;
  const offCy = (Number(visual?.card_front_offset_y) || 0) * 0.25;
  const SUPERT = ["S", "U", "P", "E", "R", "T"];
  return (
    <div
      className="mt-3 rounded-xl border border-slate-200 bg-gradient-to-br from-sky-50 to-slate-50 p-5"
      data-testid="perspective-preview"
    >
      <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3 text-center">
        Pré-visualização ao vivo · perspectiva={persp}px · roleta Y{tiltR}° X{tiltRx}° Z{tiltRz}° · cartas Y{tiltC}° X{tiltCx}° Z{tiltCz}°
      </div>
      <div className="grid grid-cols-12 gap-4 items-center min-h-[260px]">
        {/* LEFT: mini Roulette card */}
        <div className="col-span-5 flex justify-end">
          <div
            className="relative w-full max-w-[230px]"
            style={{
              perspective: `${persp}px`,
              perspectiveOrigin: "70% 50%",
              aspectRatio: "4 / 3",
              transform: `translate(${offRx}px, ${offRy}px) scale(${scaleR})`,
              transformOrigin: "center center",
            }}
          >
            <div
              className="absolute inset-0 rounded-lg overflow-hidden"
              style={{
                transform: `rotateY(${tiltR}deg) rotateX(${tiltRx}deg) rotate(${tiltRz}deg)`,
                transformStyle: "preserve-3d",
                background: "linear-gradient(180deg, #1d4ed8 0%, #173b78 55%, #0b1e3a 100%)",
                border: `${Math.max(4, (visual?.roulette_border_width ?? 22) / 4)}px solid ${visual?.roulette_border_color || "#03060f"}`,
                filter: shadow ? "drop-shadow(-10px 14px 18px rgba(15,23,42,0.25))" : "none",
              }}
            >
              <div className="h-full p-2 grid grid-cols-[1fr_1.05fr] gap-1">
                <div className="grid grid-cols-2 grid-rows-5 grid-flow-col gap-0.5">
                  {"ABCDEFGHIJ".split("").map((c) => (
                    <div key={c} className="bg-white/95 rounded-[3px] flex items-center justify-center text-[7px] font-[Fredoka] font-extrabold text-amber-500">
                      {c}
                    </div>
                  ))}
                </div>
                <div className="flex flex-col gap-1">
                  <div className="grid grid-cols-5 gap-0.5">
                    {[0, 1, 2, 3, 4].map((i) => {
                      const DEF = [
                        { from: "#FFEB3B", to: "#FFF59D" },
                        { from: "#FFC107", to: "#FF9800" },
                        { from: "#FF9800", to: "#E64A19" },
                        { from: "#F44336", to: "#FF9800" },
                        { from: "#E64A19", to: "#FFEB3B" },
                      ];
                      const grads = Array.isArray(visual?.cycle_top_gradients) && visual.cycle_top_gradients.length > 0
                        ? visual.cycle_top_gradients : DEF;
                      const g = grads[i] || DEF[i];
                      return (
                        <div
                          key={i}
                          className="rounded-[2px] bg-white relative overflow-hidden"
                          style={{ aspectRatio: "1 / 1.7" }}
                        >
                          <div
                            className="absolute top-0 left-0 right-0 h-[16%]"
                            style={{
                              background: `linear-gradient(180deg, ${g.from} 0%, ${g.to} 100%)`,
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex-1 bg-white rounded-md grid place-items-center">
                    <span className="font-[Fredoka] font-extrabold text-amber-400" style={{ fontSize: "42px", lineHeight: 1 }}>
                      U
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: mini Question card */}
        <div className="col-span-7 flex justify-start">
          <div
            className="relative w-full max-w-[360px]"
            style={{
              perspective: `${persp}px`,
              perspectiveOrigin: "30% 50%",
              aspectRatio: "1.414 / 1",
              transform: `translate(${offCx}px, ${offCy}px) scale(${scaleC})`,
              transformOrigin: "center center",
            }}
          >
            <div
              className="absolute inset-0 rounded-lg overflow-hidden"
              style={{
                transform: `rotateY(${tiltC}deg) rotateX(${tiltCx}deg) rotate(${tiltCz}deg)`,
                transformStyle: "preserve-3d",
                background: "linear-gradient(180deg, #1d4ed8 0%, #173b78 55%, #0b1e3a 100%)",
                border: `${Math.max(4, (visual?.card_front_border_width ?? 22) / 4)}px solid ${visual?.card_front_border_color || "#03060f"}`,
                filter: shadow ? "drop-shadow(12px 14px 18px rgba(15,23,42,0.25))" : "none",
              }}
            >
              <div className="grid grid-cols-2 grid-rows-5 grid-flow-col gap-1 h-full p-2 pb-1">
                {"ABCDEFGHIJ".split("").map((pos) => (
                  <div key={pos} className="flex items-center gap-1">
                    <span className="font-[Fredoka] font-extrabold text-amber-400 text-[11px] w-3 text-center">{pos}</span>
                    <div className="flex-1 bg-white rounded text-[9px] text-slate-600 px-1 py-0.5 truncate">
                      {pos === "A" ? "8 + 7" : pos === "B" ? "17 − 9" : pos === "C" ? "12 + 5" : "—"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="text-[11px] text-slate-500 mt-2 text-center">
        Mexe nos sliders acima — a previsão atualiza em tempo real.
      </div>
    </div>
  );
}



/**
 * CardLogoPreview — mock card showing exactly where the logo will land
 * on the real GamePlay card using the SAME right/bottom offset math as
 * `RouletteCard` / `CardFront` in gameplay/parts.jsx.
 *
 * The card itself is a lightweight wireframe (grid of fake question
 * slots for Carta Perguntas, A-J column + big letter area for Carta
 * Roleta) — the admin's focus is the logo placement, not the card
 * contents.
 */
function CardLogoPreview({ label, image, enabled = true, height, offsetX, offsetY, hue, saturate, brightness, invert, variant }) {
  const filter = `hue-rotate(${hue || 0}deg) saturate(${saturate ?? 1}) brightness(${brightness ?? 1})${invert ? " invert(1)" : ""}`;
  return (
    <div data-testid={`card-logo-preview-${variant}`}>
      <div className="text-[10px] font-bold text-slate-600 uppercase tracking-wide mb-1 flex items-center justify-between">
        <span>{label}</span>
        {!enabled && (
          <span className="text-[9px] font-bold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded-full normal-case tracking-normal">
            logo oculto
          </span>
        )}
      </div>
      <div
        className="relative w-full rounded-xl overflow-hidden border-2 border-slate-800 shadow-lg"
        style={{
          aspectRatio: "1.4 / 1",
          background: "linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 45%, #2563eb 100%)",
        }}
      >
        {/* Diagonal stripes to mimic the real card */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "repeating-linear-gradient(135deg, rgba(255,255,255,.10) 0 3px, transparent 3px 10px)",
            opacity: 0.55,
          }}
        />
        {/* Card-specific wireframe content */}
        {variant === "front" ? (
          <div className="absolute inset-2 grid grid-cols-2 grid-rows-5 gap-1">
            {["A","B","C","D","E","F","G","H","I","J"].map((L) => (
              <div key={L} className="flex items-center gap-1">
                <span className="text-amber-300 font-black font-[Fredoka] text-[11px]">{L}</span>
                <div className="flex-1 bg-white/90 rounded-sm h-2" />
              </div>
            ))}
          </div>
        ) : (
          <div className="absolute inset-2 flex items-stretch gap-1">
            <div className="w-[38%] grid grid-cols-2 grid-rows-5 gap-0.5">
              {["A","F","B","G","C","H","D","I","E","J"].map((L, i) => (
                <div key={i} className="flex items-center gap-0.5">
                  <span className="text-amber-300 font-black font-[Fredoka] text-[9px]">{L}</span>
                  <div className="flex-1 bg-white/90 rounded-sm h-1.5" />
                </div>
              ))}
            </div>
            <div className="flex-1 bg-white/95 rounded-md grid place-items-center">
              <span className="text-amber-500 font-black font-[Fredoka] text-5xl drop-shadow">S</span>
            </div>
          </div>
        )}
        {/* The actual positioned logo — absolute right/bottom + height,
            with the same colour filters applied in-game. Hidden entirely
            when the per-card toggle is off. */}
        {enabled && (
          <img
            src={image}
            alt="logo"
            data-testid={`card-logo-img-${variant}`}
            className="absolute pointer-events-none"
            style={{
              right: `${offsetX}px`,
              bottom: `${offsetY}px`,
              height: `${height}px`,
              width: "auto",
              filter,
            }}
          />
        )}
      </div>
      <div className="text-[10px] text-slate-500 mt-1 font-mono text-center">
        H={height}px · X={offsetX}px · Y={offsetY}px
      </div>
    </div>
  );
}
